/**
 * SSParties — Party Explorer (ss* schema — complete endpoint coverage)
 *
 * Covers §6.1 + §6.2 of PARTIES_MODULE_TECHNICAL_DESIGN:
 *   Party CRUD (create, read, update, close, restore)
 *   Image upload / view
 *   Custom-data JSON patch
 *   Deduplication candidates + merge workflow
 *   Full enrichment bundle — tabs:
 *     Overview · Addresses · Contacts · Identifiers · Roles
 *     Relationships · Documents · Audit History · Image · Custom Data
 *
 * UI audit items implemented here:
 *   • SSGlobalSearch — top-bar grouped typeahead (⌘K)
 *   • All | Persons | Orgs tab strip replacing the type <Select>
 *   • Documents tab wired to SSDocumentsTab
 *   • Audit tab upgraded to SSAuditTimeline
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ssPartyAPI, ssLookupAPI, ssAuditAPI, ssRelAPI } from '../../services/ssApi';
import SSGlobalSearch     from './components/SSGlobalSearch';
import SSAuditTimeline    from './components/SSAuditTimeline';
import SSDocumentsTab     from './components/SSDocumentsTab';
import SSRelationshipGraph from './components/SSRelationshipGraph';
import {
  Page, PageHeader, Card, Modal, TabBar, Section,
  Button, Badge, Field, Input, Select, Textarea,
  Spinner, EmptyState, ErrorBanner, SuccessBanner, InfoBanner,
  DataTable, InfoGrid, Pagination, StatBar, SearchBar,
  PartyTypePill, ActiveBadge, PartyRoleChips, EventTypeBadge,
  COLOR, FONT, RADIUS,
} from '../../components/ss';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtErr = (d, fb) => {
  if (!d) return fb;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join('; ');
  // d.detail can itself be a FastAPI validation ARRAY — never return it raw,
  // or React crashes trying to render the objects.
  if (Array.isArray(d.detail)) return d.detail.map(e => e?.msg || JSON.stringify(e)).join('; ');
  if (typeof d.detail === 'string' && d.detail) return d.detail;
  return d.message || JSON.stringify(d);
};
const fmtDate = (dt) => dt ? new Date(dt).toLocaleString() : '—';

const PARTY_TYPES = ['PER', 'ORG'];
const GENDERS = [{ v: 'M', l: 'Male' }, { v: 'F', l: 'Female' }, { v: 'O', l: 'Other' }];

const BLANK_PARTY   = { type: 'PER', firstname: '', lastname: '', name: '', gender: '', birthdate: '', notes: '' };
const BLANK_ADDR    = { addresstypeid: '', address1: '', address2: '', city: '', state: '', country: '', postalcode: '', ismain: false };
const BLANK_CONTACT = { contactmediaid: '', contactmediatypeid: '', value: '', isprimary: false };
const BLANK_IDENT   = { identifiertypeid: '', value: '', issuanceauthority: '', issuancedate: '', expirationdate: '' };

// ─────────────────────────────────────────────────────────────────────────────
// Create / Edit Party modal
//   • Edit  → basic demographic fields only (backend ignores nested children on update)
//   • Create → full robust capture: Basic + Identifiers + Contacts + Addresses
//   • Create UI follows the "Benevita Polar" design spec
// ─────────────────────────────────────────────────────────────────────────────
const BVP = {
  primary: '#7B1E3C', primaryLight: '#94335A', navy: '#2B1B22', teal: '#00897B',
  muted: '#8b7681', border: '#ECE3E7', softBg: '#f7f3f4', inputBorder: '#ECE3E7',
  focusRing: 'rgba(123,30,60,.18)',
};
const BVP_CSS = `
  @keyframes bvpFadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
  .bvp-create { font-family: Inter, system-ui, -apple-system, sans-serif; }
  @media (prefers-reduced-motion: no-preference) { .bvp-create { animation: bvpFadeIn 200ms ease-out; } }
  .bvp-create input, .bvp-create select, .bvp-create textarea {
    border-radius: 10px !important; border-color: ${BVP.inputBorder} !important;
  }
  .bvp-create input:focus, .bvp-create select:focus, .bvp-create textarea:focus {
    outline: none !important; border-color: ${BVP.primary} !important;
    box-shadow: 0 0 0 3px ${BVP.focusRing} !important;
  }
  .bvp-grid2 { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
  @media (max-width: 640px) { .bvp-grid2 { grid-template-columns: 1fr; } }
`;

function BvpSectionCard({ icon, title, children, style }) {
  return (
    <div style={{ background: BVP.softBg, borderRadius: 12, padding: 16,
                  border: `1px solid ${BVP.border}`, ...style }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: BVP.navy, marginBottom: 12,
                    display: 'flex', alignItems: 'center', gap: 6 }}>
        <span aria-hidden="true" style={{ fontSize: 13 }}>{icon}</span>{title}
      </div>
      {children}
    </div>
  );
}

export function PartyFormModal({ open, onClose, onDone, initial, lookups: lookupsProp, defaultRoleCode }) {
  const isEdit = !!initial;
  const [form, setForm]           = useState(BLANK_PARTY);
  const [idents, setIdents]       = useState([]);   // [{identifiertypeid, value, issuanceauthority, expirationdate, ismain}]
  const [contacts, setContacts]   = useState([]);   // [{contactmediaid, value, isprimary}]
  const [addresses, setAddresses] = useState([]);   // [{address1, city, state, country, postalcode, ismain}]
  const [saving, setSaving]       = useState(false);
  const [err, setErr]             = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]); // role codes to assign on create

  // Self-fetch lookups when the parent doesn't supply them (e.g. edit modal, external callers)
  const EMPTY_LOOKUPS = { partyRoles: [], identTypes: [], contactMedia: [], contactMediaTypes: [], addrTypes: [], relTypes: [] };
  const [fetchedLookups, setFetchedLookups] = useState(EMPTY_LOOKUPS);

  const lookupsEmpty = !lookupsProp || (
    !lookupsProp.partyRoles?.length &&
    !lookupsProp.identTypes?.length &&
    !lookupsProp.contactMedia?.length &&
    !lookupsProp.addrTypes?.length
  );

  useEffect(() => {
    if (!open || !lookupsEmpty) return;
    Promise.all([
      ssLookupAPI.partyRoles().catch(()            => ({ data: [] })),
      ssLookupAPI.identifierTypes().catch(()       => ({ data: [] })),
      ssLookupAPI.contactMedia().catch(()          => ({ data: [] })),
      ssLookupAPI.listContactMediaTypes().catch(() => ({ data: [] })),
      ssLookupAPI.addressTypes().catch(()          => ({ data: [] })),
      ssLookupAPI.listRelTypes().catch(()          => ({ data: [] })),
    ]).then(([pr, it, cm, cmt, at, rt]) => setFetchedLookups({
      partyRoles:        pr.data  || [],
      identTypes:        it.data  || [],
      contactMedia:      cm.data  || [],
      contactMediaTypes: cmt.data || [],
      addrTypes:         at.data  || [],
      relTypes:          rt.data  || [],
    }));
  }, [open, lookupsEmpty]);

  // Use prop lookups when provided, otherwise fall back to self-fetched ones
  const lookups = lookupsEmpty ? fetchedLookups : lookupsProp;

  useEffect(() => {
    if (initial) {
      setForm({
        type:      initial.type || 'PER',
        firstname: initial.firstname || '',
        middlename:initial.middlename || '',
        lastname:  initial.lastname || '',
        name:      initial.name || '',
        gender:    initial.gender || '',
        birthdate: initial.birthdate ? initial.birthdate.split('T')[0] : '',
        notes:     initial.notes || '',
      });
    } else {
      setForm(BLANK_PARTY);
      setIdents([]); setContacts([]); setAddresses([]);
      setSelectedRoles(defaultRoleCode ? [defaultRoleCode] : []);
    }
    setErr('');
  }, [initial, open, defaultRoleCode]);

  const upd = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  // Row helpers for nested collections
  const addIdent    = () => setIdents(x  => [...x, { ...BLANK_IDENT }]);
  const updIdent    = (i, k, v) => setIdents(x  => x.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const rmIdent     = (i) => setIdents(x  => x.filter((_, idx) => idx !== i));

  const addContact  = () => setContacts(x => [...x, { ...BLANK_CONTACT }]);
  const updContact  = (i, k, v) => setContacts(x => x.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const rmContact   = (i) => setContacts(x => x.filter((_, idx) => idx !== i));

  const addAddress   = () => setAddresses(x => [...x, { ...BLANK_ADDR }]);
  const updAddress   = (i, k, v) => setAddresses(x => x.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  // Patch multiple keys in one render — used by LocationFields cascading updates.
  const patchAddress = (i, patch) => setAddresses(x => x.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const rmAddress    = (i) => setAddresses(x => x.filter((_, idx) => idx !== i));

  const identTypes   = (lookups && lookups.identTypes)   || [];
  const contactMedia = (lookups && lookups.contactMedia) || [];
  const partyRoles   = (lookups && lookups.partyRoles)   || [];
  const addrTypes    = (lookups && lookups.addrTypes)    || [];

  // Roles valid for the currently selected party type (PER / ORG / both)
  const roleAllowed = (r) =>
    !r.allowedpartytype || r.allowedpartytype === form.type || r.allowedpartytype === 'PER+ORG';
  const visibleRoles = partyRoles.filter(roleAllowed);
  const toggleRole = (code) =>
    setSelectedRoles(s => s.includes(code) ? s.filter(c => c !== code) : [...s, code]);
  // When the user switches PER/ORG, drop roles that are no longer valid for the type
  useEffect(() => {
    if (!partyRoles.length) return;
    setSelectedRoles(s => s.filter(code => {
      const r = partyRoles.find(x => x.code === code);
      return !r || roleAllowed(r);
    }));
  }, [form.type]);

  const submit = async e => {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      // Convert empty strings to null so Postgres date/timestamp columns
      // don't receive '' (which causes "invalid input syntax for type timestamp").
      const nullify = (v) => (v === '' || v === undefined ? null : v);
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, nullify(v)])
      );
      if (payload.type === 'PER') delete payload.name;
      else { delete payload.firstname; delete payload.middlename; delete payload.lastname; delete payload.gender; delete payload.birthdate; }

      if (isEdit) {
        await ssPartyAPI.update(initial.partyid, payload);
      } else {
        // Attach nested enrichment on create
        const cleanIdents = idents
          .filter(i => i.identifiertypeid && i.value)
          .map(i => ({
            identifiertypeid: Number(i.identifiertypeid),
            value: i.value,
            issuanceauthority: i.issuanceauthority || null,
            expirationdate:    i.expirationdate    || null,
            ismain: !!i.ismain,
          }));
        const cleanContacts = contacts
          .filter(c => c.contactmediaid && c.value)
          .map(c => ({
            contactmediaid:     Number(c.contactmediaid),
            contactmediatypeid: c.contactmediatypeid ? Number(c.contactmediatypeid) : null,
            value:    c.value,
            isprimary: !!c.isprimary,
          }));
        const addrTypeNum = (v) => { const n = Number(v); return isNaN(n) || n === 0 ? null : n; };
        const cleanAddresses = addresses
          .filter(a => a.address1 || a.city || a.country)
          .map(a => ({
            addresstypeid: addrTypeNum(a.addresstypeid),
            address1:   a.address1   || null,
            city:       a.city       || null,
            state:      a.state      || null,
            country:    a.country    || null,
            postalcode: a.postalcode || null,
            ismain: !!a.ismain,
          }));

        payload.identifiers = cleanIdents;
        payload.contacts    = cleanContacts;
        payload.addresses   = cleanAddresses;

        const created = await ssPartyAPI.create(payload);
        // Assign every selected business role (+ defaultRoleCode for backward
        // safety, e.g. the list filtered by role_code when viewing Patients).
        // Deduped; each assignment is non-fatal — the party is already created.
        const roleCodes = [...new Set([...selectedRoles, ...(defaultRoleCode ? [defaultRoleCode] : [])])];
        const newId = created?.data?.partyid || created?.partyid;
        if (newId) {
          for (const code of roleCodes) {
            try { await ssPartyAPI.assignPartyRole(newId, { role_code: code }); }
            catch (_) { /* non-fatal — party is created; role assignment failure is surfaced elsewhere */ }
          }
        }
        // Professional roles need a registry profile — surface a hint on success
        const profHint = roleCodes.includes('NURSE') || roleCodes.includes('DOCTOR')
          ? 'Party created. A Nurse/Doctor role was assigned — complete the professional profile from the Nurses/Doctors registry.'
          : '';
        onDone(newId, profHint);
        return;
      }
      onDone(null);
    } catch (ex) { setErr(fmtErr(ex.response?.data, 'Failed to save party')); }
    finally { setSaving(false); }
  };

  const sectionTitle = (label) => (
    <div style={{ fontSize: 12, fontWeight: 700, color: COLOR.textMuted, textTransform: 'uppercase',
                  letterSpacing: '0.6px', margin: '18px 0 8px', paddingBottom: 6,
                  borderBottom: `1px solid ${COLOR.grayBorder}` }}>{label}</div>
  );

  // Column section card — uniform Polar styling for the 3 nested sections
  // (create only). Deliberately NO per-column accent colors: one brand,
  // one palette — same treatment as the Identity/Details cards above.
  const columnCard = (headerNode, children) => (
    <div style={{
      background: BVP.softBg,
      border: `1px solid ${BVP.border}`,
      borderRadius: 12,
      padding: 16,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      overflowY: 'auto',
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: BVP.navy, marginBottom: 10,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {headerNode}
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
    </div>
  );

  const isPatientCtx = defaultRoleCode === 'PATIENT';

  return (
    <Modal
      open={open}
      title={isEdit ? `Edit Party #${initial?.partyid}` : (isPatientCtx ? 'Register Patient' : 'New Party')}
      onClose={onClose}
      width={isEdit ? 560 : '95vw'}
      fullscreen={!isEdit}
    >
      {!isEdit && <style>{BVP_CSS}</style>}
      <ErrorBanner>{err}</ErrorBanner>
      <form onSubmit={submit} className={!isEdit ? 'bvp-create' : undefined}
            style={{ display: 'flex', flexDirection: 'column', gap: 16, minHeight: 0, flex: 1 }}>
        {/* ── Header banner ────────────────────────────────────────────────── */}
        {!isEdit && (
          <div style={{
            background: `linear-gradient(135deg, ${BVP.navy}, ${BVP.primary})`,
            borderRadius: 14, padding: '16px 18px', color: '#fff',
            display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <div aria-hidden="true" style={{
              width: 42, height: 42, borderRadius: 12, background: 'rgba(255,255,255,.14)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0,
            }}>{isPatientCtx ? '🩺' : '✨'}</div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.25 }}>
                {isPatientCtx ? 'Register a new patient' : 'Create a new person/organisation'}
              </div>
              <div style={{ fontSize: 12.5, opacity: .82, marginTop: 2 }}>
                {isPatientCtx
                  ? 'The new record is automatically assigned the Patient role.'
                  : 'Capture identity details, plus optional identifiers, contacts and addresses.'}
              </div>
            </div>
          </div>
        )}

        {/* ── Party type selector cards ────────────────────────────────────── */}
        {!isEdit && (
          <div className="bvp-grid2" role="radiogroup" aria-label="Party Type">
            {PARTY_TYPES.map(t => {
              const sel = form.type === t;
              return (
                <button key={t} type="button" role="radio" aria-checked={sel}
                  onClick={() => setForm(f => ({ ...f, type: t }))}
                  style={{
                    position: 'relative', textAlign: 'left', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
                    borderRadius: 12, background: sel ? 'rgba(123,30,60,.06)' : '#fff',
                    border: `2px solid ${sel ? BVP.primary : BVP.border}`,
                    transition: 'border-color .15s, background .15s',
                  }}>
                  <span aria-hidden="true" style={{
                    width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                    background: sel ? 'rgba(123,30,60,.12)' : BVP.softBg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                  }}>{t === 'PER' ? '👤' : '🏢'}</span>
                  <span>
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: BVP.navy }}>
                      {t === 'PER' ? 'Person' : 'Organisation'}
                    </span>
                    <span style={{ display: 'block', fontSize: 12, color: BVP.muted, marginTop: 1 }}>
                      {t === 'PER' ? 'An individual — patient, caregiver, contact.' : 'A company, facility or institution.'}
                    </span>
                  </span>
                  {sel && (
                    <span aria-hidden="true" style={{
                      position: 'absolute', top: 10, right: 10, width: 18, height: 18, borderRadius: '50%',
                      background: BVP.primary, color: '#fff', fontSize: 11, lineHeight: '18px', textAlign: 'center',
                    }}>✓</span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* ── Section · Identity + Details ─────────────────────────────────── */}
        {!isEdit && (
          <div className="bvp-grid2" style={{ alignItems: 'start' }}>
            <BvpSectionCard icon="🪪" title="Identity">
              <div className="bvp-grid2">
                {form.type === 'PER' ? (<>
                  <Field label="First Name" required style={{ marginBottom: 0 }}>
                    <Input required value={form.firstname} onChange={upd('firstname')} />
                  </Field>
                  <Field label="Middle Name" style={{ marginBottom: 0 }}>
                    <Input value={form.middlename || ''} onChange={upd('middlename')} />
                  </Field>
                  <Field label="Last Name" style={{ marginBottom: 0 }}>
                    <Input value={form.lastname} onChange={upd('lastname')} />
                  </Field>
                  <Field label="Gender" style={{ marginBottom: 0 }}>
                    <div style={{
                      display: 'flex', gap: 4, background: '#fff', padding: 4,
                      borderRadius: 10, border: `1px solid ${BVP.inputBorder}`,
                    }} role="radiogroup" aria-label="Gender">
                      {GENDERS.map(g => {
                        const sel = form.gender === g.v;
                        return (
                          <button key={g.v} type="button" role="radio" aria-checked={sel}
                            onClick={() => setForm(f => ({ ...f, gender: sel ? '' : g.v }))}
                            style={{
                              flex: 1, padding: '6px 8px', borderRadius: 7, fontSize: 12.5,
                              fontWeight: sel ? 700 : 500, cursor: 'pointer', border: 'none',
                              background: sel ? BVP.primary : 'transparent',
                              color: sel ? '#fff' : BVP.muted, transition: 'background .15s, color .15s',
                            }}>{g.l}</button>
                        );
                      })}
                    </div>
                  </Field>
                  <Field label="Birthdate" style={{ marginBottom: 0 }}>
                    <Input type="date" value={form.birthdate} onChange={upd('birthdate')} />
                  </Field>
                </>) : (
                  <Field label="Organisation Name" required style={{ gridColumn: '1/-1', marginBottom: 0 }}>
                    <Input required value={form.name} onChange={upd('name')} />
                  </Field>
                )}
              </div>
            </BvpSectionCard>
            <BvpSectionCard icon="📝" title="Details">
              <Field label="Notes" style={{ marginBottom: 0 }}>
                <Textarea value={form.notes} onChange={upd('notes')} rows={form.type === 'PER' ? 6 : 3} />
              </Field>
            </BvpSectionCard>
          </div>
        )}

        {/* ── Section · Business roles (create only) ───────────────────────── */}
        {!isEdit && (
          <BvpSectionCard icon="🏷️" title="Business roles">
            <div style={{ fontSize: 12, color: BVP.muted, marginTop: -6, marginBottom: 10, lineHeight: 1.5 }}>
              Roles define what this party is in the system — e.g. a person becomes a Patient by holding
              the PATIENT role. You can also assign roles later from the party's detail panel.
            </div>
            {visibleRoles.length === 0 ? (
              <div style={{ fontSize: 12, color: BVP.muted }}>
                No roles available for this party type.
              </div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {visibleRoles.map(r => {
                  const active = selectedRoles.includes(r.code);
                  return (
                    <button key={r.code} type="button" onClick={() => toggleRole(r.code)}
                      aria-pressed={active}
                      style={{
                        padding: '7px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 600,
                        cursor: 'pointer', transition: 'all .15s',
                        background: active ? 'rgba(123,30,60,.10)' : '#F1F5FA',
                        border: `1.5px solid ${active ? BVP.primary : 'transparent'}`,
                        color: active ? BVP.primary : BVP.muted,
                      }}>{active ? '✓ ' : ''}{r.name}</button>
                  );
                })}
              </div>
            )}
          </BvpSectionCard>
        )}

        {isEdit && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {form.type === 'PER' ? (<>
              <Field label="First Name" required style={{ marginBottom: 10 }}>
                <Input required value={form.firstname} onChange={upd('firstname')} />
              </Field>
              <Field label="Last Name" style={{ marginBottom: 10 }}>
                <Input value={form.lastname} onChange={upd('lastname')} />
              </Field>
              <Field label="Middle Name" style={{ marginBottom: 10 }}>
                <Input value={form.middlename || ''} onChange={upd('middlename')} />
              </Field>
              <Field label="Gender" style={{ marginBottom: 10 }}>
                <Select value={form.gender} onChange={upd('gender')}>
                  <option value="">— Select —</option>
                  {GENDERS.map(g => <option key={g.v} value={g.v}>{g.l}</option>)}
                </Select>
              </Field>
              <Field label="Birthdate" style={{ marginBottom: 10 }}>
                <Input type="date" value={form.birthdate} onChange={upd('birthdate')} />
              </Field>
            </>) : (
              <Field label="Organization Name" required style={{ gridColumn: '1/-1', marginBottom: 10 }}>
                <Input required value={form.name} onChange={upd('name')} />
              </Field>
            )}
            <Field label="Notes" style={{ gridColumn: '1/-1', marginBottom: 10 }}>
              <Textarea value={form.notes} onChange={upd('notes')} rows={2} />
            </Field>
          </div>
        )}

        {/* ── Enrichment: Identifiers + Contacts + Addresses side-by-side on create ── */}
        {!isEdit && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: 14,
            flex: 1,
            minHeight: 0,
          }}>

            {/* ── Identifiers column ── */}
            {columnCard(
              <>
                <span>🪪 Identifiers {idents.length > 0 && <Badge color="blue" style={{ marginLeft: 6 }}>{idents.length}</Badge>}</span>
                <Button type="button" size="sm" variant="ghost" onClick={addIdent}>+ Add</Button>
              </>,
              <>
                {idents.length === 0 && <div style={{ color: COLOR.textMuted, fontSize: 12, padding: '14px 0' }}>No identifiers yet. Click + Add to attach one.</div>}
                {idents.map((row, i) => {
                  const sel = identTypes.find(t => String(t.id) === String(row.identifiertypeid));
                  return (
                    <div key={i} style={{ background: '#fff', border: `1px solid ${BVP.border}`, borderRadius: 10, padding: 10, marginBottom: 8 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
                        <Field label="Type" style={{ marginBottom: 0 }}>
                          <Select value={row.identifiertypeid} onChange={e => updIdent(i, 'identifiertypeid', e.target.value)}>
                            <option value="">Select…</option>
                            {identTypes
                              .filter(t => !t.allowedpartytype || t.allowedpartytype === form.type || t.allowedpartytype === 'PER+ORG')
                              .map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </Select>
                        </Field>
                        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                          <Button type="button" variant="danger" size="sm" onClick={() => rmIdent(i)}>✕</Button>
                        </div>
                      </div>
                      <Field label="Value" style={{ marginBottom: 0, marginTop: 8 }}>
                        <Input value={row.value} onChange={e => updIdent(i, 'value', e.target.value)} />
                      </Field>
                      {(sel?.requireissuanceauthority || sel?.requireexpirationdate) && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                          {sel?.requireissuanceauthority && (
                            <Field label="Issuing Authority" style={{ marginBottom: 0 }}>
                              <Input value={row.issuanceauthority || ''} onChange={e => updIdent(i, 'issuanceauthority', e.target.value)} />
                            </Field>
                          )}
                          {sel?.requireexpirationdate && (
                            <Field label="Expires" style={{ marginBottom: 0 }}>
                              <Input type="date" value={row.expirationdate || ''} onChange={e => updIdent(i, 'expirationdate', e.target.value)} />
                            </Field>
                          )}
                        </div>
                      )}
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 8 }}>
                        <input type="checkbox" checked={!!row.ismain} onChange={e => updIdent(i, 'ismain', e.target.checked)} />
                        Primary / main identifier
                      </label>
                    </div>
                  );
                })}
              </>
            )}

            {/* ── Contacts column ── */}
            {columnCard(
              <>
                <span>📞 Contacts {contacts.length > 0 && <Badge color="blue" style={{ marginLeft: 6 }}>{contacts.length}</Badge>}</span>
                <Button type="button" size="sm" variant="ghost" onClick={addContact}>+ Add</Button>
              </>,
              <>
                {contacts.length === 0 && <div style={{ color: COLOR.textMuted, fontSize: 12, padding: '14px 0' }}>No contacts yet. Click + Add to attach one.</div>}
                {contacts.map((row, i) => (
                  <div key={i} style={{ background: '#fff', border: `1px solid ${BVP.border}`, borderRadius: 10, padding: 10, marginBottom: 8 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
                      <Field label="Channel" style={{ marginBottom: 0 }}>
                        <Select value={row.contactmediaid} onChange={e => updContact(i, 'contactmediaid', e.target.value)}>
                          <option value="">Select…</option>
                          {contactMedia.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </Select>
                      </Field>
                      <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <Button type="button" variant="danger" size="sm" onClick={() => rmContact(i)}>✕</Button>
                      </div>
                    </div>
                    <Field label="Value" style={{ marginBottom: 0, marginTop: 8 }}>
                      <Input value={row.value} onChange={e => updContact(i, 'value', e.target.value)} placeholder="phone / email / handle" />
                    </Field>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 8 }}>
                      <input type="checkbox" checked={!!row.isprimary} onChange={e => updContact(i, 'isprimary', e.target.checked)} />
                      Primary contact
                    </label>
                  </div>
                ))}
              </>
            )}

            {/* ── Addresses column ── */}
            {columnCard(
              <>
                <span>📍 Addresses {addresses.length > 0 && <Badge color="blue" style={{ marginLeft: 6 }}>{addresses.length}</Badge>}</span>
                <Button type="button" size="sm" variant="ghost" onClick={addAddress}>+ Add</Button>
              </>,
              <>
                {addresses.length === 0 && <div style={{ color: COLOR.textMuted, fontSize: 12, padding: '14px 0' }}>No addresses yet. Click + Add to attach one.</div>}
                {addresses.map((row, i) => (
                  <div key={i} style={{ background: '#fff', border: `1px solid ${BVP.border}`, borderRadius: 10, padding: 10, marginBottom: 8 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
                      <Field label="Address Type" style={{ marginBottom: 0 }}>
                        <Select value={row.addresstypeid || ''} onChange={e => updAddress(i, 'addresstypeid', e.target.value)}>
                          <option value="">— Select type —</option>
                          {addrTypes.map(t => (
                            <option key={t.addresstypeid || t.id} value={t.addresstypeid || t.id}>{t.name}</option>
                          ))}
                        </Select>
                      </Field>
                      <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <Button type="button" variant="danger" size="sm" onClick={() => rmAddress(i)}>✕</Button>
                      </div>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <Field label="Street Address" style={{ marginBottom: 0 }}>
                        <Input value={row.address1} onChange={e => updAddress(i, 'address1', e.target.value)} />
                      </Field>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <LocationFields
                        value={{ country: row.country, state: row.state, city: row.city, postalcode: row.postalcode }}
                        onChange={(next) => patchAddress(i, next)}
                      />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 8 }}>
                      <input type="checkbox" checked={!!row.ismain} onChange={e => updAddress(i, 'ismain', e.target.checked)} />
                      Main address
                    </label>
                  </div>
                ))}
              </>,
              '#f59e0b'
            )}

          </div>
        )}

        <div style={{
          marginTop: 'auto',
          display: 'flex',
          gap: 8,
          borderTop: `1px solid ${COLOR.grayBorder}`,
          paddingTop: 14,
          position: !isEdit ? 'sticky' : 'static',
          bottom: !isEdit ? 0 : 'auto',
          background: '#fff',
          justifyContent: 'flex-end',
        }}>
          {isEdit ? (<>
            <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</Button>
          </>) : (<>
            <button type="button" onClick={onClose} style={{
              padding: '9px 18px', borderRadius: 10, fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
              background: 'transparent', border: `1px solid ${BVP.border}`, color: BVP.muted,
              marginRight: 'auto',
            }}>Cancel</button>
            <button type="submit" disabled={saving} style={{
              padding: '9px 22px', borderRadius: 10, fontSize: 13.5, fontWeight: 700,
              cursor: saving ? 'default' : 'pointer', border: 'none', color: '#fff',
              background: `linear-gradient(135deg, ${BVP.primary}, ${BVP.primaryLight})`,
              opacity: saving ? .45 : 1, boxShadow: '0 2px 8px rgba(123,30,60,.25)',
            }}>
              {saving ? 'Saving…' : (defaultRoleCode === 'PATIENT' ? 'Register patient ✓' : 'Create party ✓')}
            </button>
          </>)}
        </div>
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Merge modal — 3-step workflow: Candidates → Side-by-side diff → Dry-run/Confirm
// ─────────────────────────────────────────────────────────────────────────────

const MERGE_FIELD_LABELS = [
  { key: 'type',        label: 'Party Type' },
  { key: 'firstname',   label: 'First Name' },
  { key: 'lastname',    label: 'Last Name' },
  { key: 'name',        label: 'Organisation Name' },
  { key: 'birthdate',   label: 'Date of Birth' },
  { key: 'gender',      label: 'Gender' },
  { key: 'email',       label: 'Email' },
  { key: 'ownercompany',label: 'Company' },
  { key: 'datefrom',    label: 'Active From' },
  { key: 'dateto',      label: 'Active To' },
];

function MergeFieldDiff({ source, target }) {
  if (!source || !target) return null;
  const diffStyle = { background: '#fff3cd', borderRadius: 3, padding: '1px 4px' };
  const sameStyle = { color: COLOR.textMuted };

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ borderBottom: `2px solid ${COLOR.border}` }}>
          <th style={{ textAlign: 'left', padding: '4px 8px', width: '20%', color: COLOR.textMuted }}>Field</th>
          <th style={{ textAlign: 'left', padding: '4px 8px', width: '40%' }}>
            Source #{source.partyid} <Badge color="red">will be closed</Badge>
          </th>
          <th style={{ textAlign: 'left', padding: '4px 8px', width: '40%' }}>
            Target #{target.partyid} <Badge color="green">survives</Badge>
          </th>
        </tr>
      </thead>
      <tbody>
        {MERGE_FIELD_LABELS.map(({ key, label }) => {
          const sv = source[key] != null ? String(source[key]) : '—';
          const tv = target[key] != null ? String(target[key]) : '—';
          const differs = sv !== tv;
          return (
            <tr key={key} style={{ borderBottom: `1px solid ${COLOR.border}` }}>
              <td style={{ padding: '4px 8px', color: COLOR.textMuted, fontWeight: 500 }}>{label}</td>
              <td style={{ padding: '4px 8px' }}>
                <span style={differs ? diffStyle : sameStyle}>{sv}</span>
              </td>
              <td style={{ padding: '4px 8px' }}>
                <span style={differs ? { fontWeight: 600 } : sameStyle}>{tv}</span>
              </td>
            </tr>
          );
        })}
        <tr style={{ borderBottom: `1px solid ${COLOR.border}` }}>
          <td style={{ padding: '4px 8px', color: COLOR.textMuted, fontWeight: 500 }}>Addresses</td>
          <td style={{ padding: '4px 8px', color: COLOR.textMuted }}>{(source.addresses || []).length} record(s)</td>
          <td style={{ padding: '4px 8px' }}>will be transferred →</td>
        </tr>
        <tr style={{ borderBottom: `1px solid ${COLOR.border}` }}>
          <td style={{ padding: '4px 8px', color: COLOR.textMuted, fontWeight: 500 }}>Identifiers</td>
          <td style={{ padding: '4px 8px', color: COLOR.textMuted }}>{(source.identifiers || []).length} record(s)</td>
          <td style={{ padding: '4px 8px' }}>will be transferred →</td>
        </tr>
        <tr>
          <td style={{ padding: '4px 8px', color: COLOR.textMuted, fontWeight: 500 }}>Relationships</td>
          <td style={{ padding: '4px 8px', color: COLOR.textMuted }}>
            {((source.relationships_out || []).length + (source.relationships_in || []).length)} edge(s)
          </td>
          <td style={{ padding: '4px 8px' }}>will be rewired →</td>
        </tr>
      </tbody>
    </table>
  );
}

function MergeModal({ partyId, open, onClose, onDone }) {
  // Step: 'candidates' → 'diff' → 'dryrun' → 'done'
  const [step, setStep]             = useState('candidates');
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [targetId, setTargetId]     = useState('');
  const [sourceDetail, setSourceDetail] = useState(null);
  const [targetDetail, setTargetDetail] = useState(null);
  const [diffLoading, setDiffLoading]   = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunning, setDryRunning]     = useState(false);
  const [merging, setMerging]       = useState(false);
  const [err, setErr]               = useState('');
  const [ok, setOk]                 = useState('');

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setStep('candidates');
    setCandidates([]);
    setTargetId('');
    setSourceDetail(null);
    setTargetDetail(null);
    setDryRunResult(null);
    setErr(''); setOk('');
    setLoading(true);
    ssPartyAPI.duplicates(partyId)
      .then(d => setCandidates(Array.isArray(d) ? d : (d.data || [])))
      .catch(ex => setErr(fmtErr(ex.response?.data, 'Failed to load candidates')))
      .finally(() => setLoading(false));
  }, [open, partyId]);

  // Load full details for both parties when moving to diff step
  const goToDiff = async (tid) => {
    if (!tid) { setErr('Select a target party first.'); return; }
    setDiffLoading(true); setErr('');
    try {
      const [src, tgt] = await Promise.all([
        ssPartyAPI.getFull(partyId),
        ssPartyAPI.getFull(Number(tid)),
      ]);
      setSourceDetail(src);
      setTargetDetail(tgt);
      setTargetId(String(tid));
      setStep('diff');
    } catch (ex) {
      setErr(fmtErr(ex.response?.data, 'Failed to load party details'));
    } finally {
      setDiffLoading(false);
    }
  };

  // Dry-run
  const doDryRun = async () => {
    setDryRunning(true); setErr(''); setDryRunResult(null);
    try {
      const result = await ssPartyAPI.mergeDryRun({
        source_id: Number(partyId),
        target_id: Number(targetId),
      });
      setDryRunResult(result);
      setStep('dryrun');
    } catch (ex) {
      setErr(fmtErr(ex.response?.data, 'Dry-run failed'));
    } finally {
      setDryRunning(false);
    }
  };

  // Execute merge
  const doMerge = async () => {
    setMerging(true); setErr('');
    try {
      await ssPartyAPI.merge({ source_id: Number(partyId), target_id: Number(targetId) });
      setOk(`Party #${partyId} successfully merged into #${targetId}.`);
      setStep('done');
      setTimeout(() => { onDone(); onClose(); }, 2000);
    } catch (ex) {
      setErr(fmtErr(ex.response?.data, 'Merge failed'));
    } finally {
      setMerging(false);
    }
  };

  const CANDIDATE_COLS = [
    { key: 'party_id', label: 'ID', render: (v, r) => v || r.partyid },
    { key: 'name',     label: 'Name', render: (v, r) => v || r.display_name },
    { key: 'rule',     label: 'Match Rule', render: v => (
      <Badge color={v === 'STRONG_ID' ? 'red' : v === 'SOFT_MATCH' ? 'amber' : 'blue'}>
        {v || 'FUZZY'}
      </Badge>
    )},
    { key: 'score',    label: 'Score', render: v => v != null ? `${v}%` : '—' },
    { key: 'detail',   label: 'Detail', render: v => <span style={{ color: COLOR.textMuted, fontSize: 12 }}>{v}</span> },
  ];

  const stepTitle = { candidates: 'Step 1 — Select Duplicate Candidate', diff: 'Step 2 — Field Comparison', dryrun: 'Step 3 — Dry-Run Preview', done: 'Merge Complete' };
  const modalWidth = step === 'diff' || step === 'dryrun' ? 900 : 700;

  return (
    <Modal open={open} title={`Merge Workflow — Party #${partyId} · ${stepTitle[step] || ''}`} onClose={onClose} width={modalWidth}>
      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, fontSize: 12 }}>
        {['candidates', 'diff', 'dryrun'].map((s, i) => (
          <span key={s} style={{
            padding: '3px 10px', borderRadius: 12,
            background: step === s ? COLOR.primary : step === 'done' ? COLOR.successBg : COLOR.bg,
            color: step === s ? '#fff' : COLOR.textMuted,
            fontWeight: step === s ? 600 : 400,
          }}>
            {i + 1}. {s === 'candidates' ? 'Select' : s === 'diff' ? 'Compare' : 'Confirm'}
          </span>
        ))}
      </div>

      <InfoBanner>
        {step === 'candidates' && 'The deduplication engine found the following potential duplicates. Select one to compare fields.'}
        {step === 'diff' && 'Review field-by-field differences. Fields highlighted in amber differ between parties. The target party survives; the source is soft-closed.'}
        {step === 'dryrun' && 'Review what will be transferred. No changes have been made yet. Click Execute Merge to proceed.'}
        {step === 'done' && 'The merge has been executed successfully.'}
      </InfoBanner>

      <ErrorBanner>{err}</ErrorBanner>
      <SuccessBanner>{ok}</SuccessBanner>

      {/* ── Step 1: Candidates ── */}
      {step === 'candidates' && (
        loading ? <Spinner /> : candidates.length === 0
          ? <EmptyState icon="✅" title="No duplicate candidates found" message="The deduplication engine (Jaro-Winkler ≥ 0.92 + strong-ID + soft-match) found no duplicates for this party." />
          : (
            <>
              <DataTable
                columns={CANDIDATE_COLS}
                rows={candidates}
                rowKey={r => r.party_id || r.partyid}
                onRowClick={r => setTargetId(String(r.party_id || r.partyid))}
                selectedKey={targetId ? Number(targetId) : undefined}
              />
              <div style={{ marginTop: 16, display: 'flex', gap: 10, alignItems: 'center' }}>
                <Select style={{ width: 220 }} value={targetId} onChange={e => setTargetId(e.target.value)}>
                  <option value="">Select target party…</option>
                  {candidates.map(c => {
                    const id = c.party_id || c.partyid;
                    return <option key={id} value={id}>#{id} — {c.name || c.display_name}</option>;
                  })}
                </Select>
                <Button onClick={() => goToDiff(targetId)} disabled={!targetId || diffLoading}>
                  {diffLoading ? 'Loading…' : 'Compare Fields →'}
                </Button>
              </div>
            </>
          )
      )}

      {/* ── Step 2: Side-by-side field diff ── */}
      {step === 'diff' && (
        <>
          <MergeFieldDiff source={sourceDetail} target={targetDetail} />
          <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
            <Button variant="ghost" onClick={() => setStep('candidates')}>← Back</Button>
            <Button onClick={doDryRun} disabled={dryRunning}>
              {dryRunning ? 'Running dry-run…' : 'Run Dry-Run Preview →'}
            </Button>
          </div>
        </>
      )}

      {/* ── Step 3: Dry-run result + confirmation ── */}
      {step === 'dryrun' && dryRunResult && (
        <>
          <div style={{
            background: COLOR.bg, border: `1px solid ${COLOR.border}`,
            borderRadius: 6, padding: 16, fontSize: 13, marginBottom: 16,
          }}>
            <strong>Dry-Run Summary</strong>
            <table style={{ width: '100%', marginTop: 10, borderCollapse: 'collapse' }}>
              <tbody>
                {[
                  ['Source party', `#${partyId}`],
                  ['Target party (survives)', `#${targetId}`],
                  ['Addresses to transfer', dryRunResult.addresses_to_transfer ?? '—'],
                  ['Contacts to transfer', dryRunResult.contacts_to_transfer ?? '—'],
                  ['Identifiers to transfer', dryRunResult.identifiers_to_transfer ?? '—'],
                  ['Relationships to rewire', dryRunResult.relationships_to_rewire ?? '—'],
                  ['User accounts affected', dryRunResult.users_affected ?? '—'],
                  ['FK tables to rewrite', dryRunResult.tables_updated ?? '—'],
                  ['Conflicts detected', dryRunResult.conflicts?.length
                    ? <Badge color="red">{dryRunResult.conflicts.length}</Badge>
                    : <Badge color="green">None</Badge>],
                ].map(([label, val]) => (
                  <tr key={label} style={{ borderBottom: `1px solid ${COLOR.border}` }}>
                    <td style={{ padding: '4px 8px', color: COLOR.textMuted, width: '40%' }}>{label}</td>
                    <td style={{ padding: '4px 8px', fontWeight: 500 }}>{val}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(dryRunResult.conflicts || []).length > 0 && (
              <div style={{ marginTop: 10, padding: 8, background: '#fff3cd', borderRadius: 4 }}>
                <strong>Conflicts:</strong>
                <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                  {dryRunResult.conflicts.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            )}
          </div>

          <div style={{ marginTop: 8, display: 'flex', gap: 10 }}>
            <Button variant="ghost" onClick={() => setStep('diff')}>← Back</Button>
            <Button
              variant="danger"
              onClick={doMerge}
              disabled={merging || (dryRunResult.conflicts || []).length > 0}
            >
              {merging ? 'Merging…' : `🔀 Execute Merge (irreversible for 30 days)`}
            </Button>
          </div>
          {(dryRunResult.conflicts || []).length > 0 && (
            <p style={{ fontSize: 12, color: COLOR.danger, marginTop: 8 }}>
              Merge is blocked due to conflicts. Resolve them before proceeding.
            </p>
          )}
        </>
      )}
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// OverflowMenu — ⋯ actions: Export JSON · Copy permalink
// ─────────────────────────────────────────────────────────────────────────────
function OverflowMenu({ partyId, party }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(party, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `party-${partyId}.json`; a.click();
    URL.revokeObjectURL(url);
    setOpen(false);
  };

  const copyPermalink = () => {
    const url = `${window.location.origin}${window.location.pathname}?party_id=${partyId}`;
    navigator.clipboard.writeText(url).catch(() => {});
    setOpen(false);
  };

  const MENU_ITEMS = [
    { icon: '⬇️', label: 'Export JSON', action: exportJson },
    { icon: '🔗', label: 'Copy permalink', action: copyPermalink },
  ];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <Button variant="ghost" size="sm" onClick={() => setOpen(o => !o)} title="More actions">⋯</Button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '110%', zIndex: 200,
          background: '#fff', border: `1px solid ${COLOR.grayBorder}`,
          borderRadius: RADIUS.md, boxShadow: '0 4px 12px rgba(0,0,0,.1)',
          minWidth: 160, overflow: 'hidden',
        }}>
          {MENU_ITEMS.map(item => (
            <button key={item.label} onClick={item.action}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                background: 'none', border: 'none', padding: '9px 14px',
                cursor: 'pointer', fontSize: 13, color: COLOR.text, textAlign: 'left',
              }}
              onMouseEnter={e => e.currentTarget.style.background = COLOR.blueBg}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              {item.icon} {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Unmerge — surfaces only when the survivor still has at least one merge log
// row inside the 30-day eligibility window.  Opens a confirm modal that lists
// each eligible victim with a live countdown, then calls
// ssPartyAPI.unmerge(mergeLogId) when the user confirms.
//
// Backend contract:
//   GET  /parties/merges?survivor_partyid=&active_only=true
//        → { data: [{ mergelogid, survivor_partyid, victim_partyid,
//                     merged_at, merge_reason, unmerge_eligible_until,
//                     unmerged_at, is_unmerge_eligible }] }
//   POST /parties/merge/{mergelogid}/unmerge
//        → 200 OK | 404 Not Found | 410 Gone (past 30-day window)
// ─────────────────────────────────────────────────────────────────────────────
function UnmergeButton({ survivorPartyId, onDone }) {
  const [eligible, setEligible] = useState([]);  // [{ mergelogid, victim_partyid, ... }]
  const [loading, setLoading]   = useState(false);
  const [open, setOpen]         = useState(false);
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState('');
  const [okMsg, setOkMsg]       = useState('');

  const fetchEligible = useCallback(async () => {
    if (!survivorPartyId) return;
    setLoading(true);
    try {
      const res = await ssPartyAPI.listMerges({
        survivor_partyid: survivorPartyId,
        active_only:      true,
      });
      const rows = (res?.data ?? res ?? []).filter(r => r?.is_unmerge_eligible);
      setEligible(rows);
    } catch (ex) {
      // Quiet failure — the affordance simply stays hidden.  An auth or
      // permission error here shouldn't block the rest of the header.
      setEligible([]);
    } finally {
      setLoading(false);
    }
  }, [survivorPartyId]);

  // Probe on mount + whenever the survivor changes so the button renders
  // exactly when the backend reports eligibility.
  useEffect(() => { fetchEligible(); }, [fetchEligible]);

  // Hide entirely when nothing to unmerge — meets the acceptance criterion
  // "render only when merged_into_partyid !== null AND within 30 days".
  if (loading) return null;
  if (!eligible.length) return null;

  const daysLeft = (iso) => {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 86400000));
  };

  const doUnmerge = async (mergelogid) => {
    setBusy(true); setErr(''); setOkMsg('');
    try {
      await ssPartyAPI.unmerge(mergelogid);
      setOkMsg(`Merge #${mergelogid} reversed.`);
      await fetchEligible();
      onDone?.();
      // Auto-close if no more eligible rows after refresh.
      setTimeout(() => setOpen(false), 800);
    } catch (ex) {
      const status = ex?.response?.status;
      if (status === 410) {
        setErr('This merge is past its 30-day reversal window.');
      } else if (status === 404) {
        setErr('Merge log entry no longer exists.');
      } else {
        setErr(fmtErr(ex?.response?.data, 'Unmerge failed'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => { setErr(''); setOkMsg(''); setOpen(true); }}
        title="Reverse a recent merge (within 30-day window)"
      >
        ↩️ Unmerge ({eligible.length})
      </Button>

      {open && (
        <Modal open onClose={() => !busy && setOpen(false)} title={`Unmerge — Party #${survivorPartyId}`} width={640}>
          <InfoBanner>
            Reversing a merge restores the original (victim) party and re-points
            its addresses, contacts, identifiers, roles, relationships, and
            user account back. Past the 30-day window the operation returns
            <code style={{ marginLeft: 4 }}>410 Gone</code>.
          </InfoBanner>

          {err   && <ErrorBanner>{err}</ErrorBanner>}
          {okMsg && <SuccessBanner>{okMsg}</SuccessBanner>}

          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {eligible.map(row => {
              const dl = daysLeft(row.unmerge_eligible_until);
              return (
                <div key={row.mergelogid}
                  style={{
                    border: `1px solid ${COLOR.grayBorder}`,
                    borderRadius: RADIUS.md,
                    padding: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}
                >
                  <div style={{ fontSize: 13, color: COLOR.text, lineHeight: 1.5 }}>
                    <div>
                      <strong>Victim party #{row.victim_partyid}</strong>
                      <span style={{ color: COLOR.textMuted, marginLeft: 8 }}>
                        merge log #{row.mergelogid}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: COLOR.textMuted, marginTop: 2 }}>
                      Merged {fmtDate(row.merged_at)}
                      {row.merge_reason ? ` · ${row.merge_reason}` : ''}
                    </div>
                    <div style={{ fontSize: 12, color: dl <= 3 ? COLOR.danger : COLOR.textMuted, marginTop: 2 }}>
                      {dl != null
                        ? `Eligible for ${dl} more day${dl === 1 ? '' : 's'} (until ${fmtDate(row.unmerge_eligible_until)})`
                        : 'Eligibility window unknown'}
                    </div>
                  </div>
                  <Button
                    variant="ghostRed"
                    size="sm"
                    disabled={busy}
                    onClick={() => {
                      if (window.confirm(
                        `Reverse merge #${row.mergelogid} and restore party #${row.victim_partyid}?`
                      )) doUnmerge(row.mergelogid);
                    }}
                  >
                    ↩️ Reverse
                  </Button>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setOpen(false)}>Close</Button>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Detail Drawer — full enrichment bundle with all tabs
// ─────────────────────────────────────────────────────────────────────────────
function DetailDrawer({ partyId, onClose, onRefresh, lookups }) {
  const [detail, setDetail]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [tab, setTab]               = useState('overview');
  const [subSaving, setSubSaving]   = useState(false);

  // Listen for QuickActionsPanel tab-switch events
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.partyId === partyId && e.detail?.tab) setTab(e.detail.tab);
    };
    window.addEventListener('ss:party:tab', handler);
    return () => window.removeEventListener('ss:party:tab', handler);
  }, [partyId]);
  const [subErr, setSubErr]         = useState('');
  const [subOk, setSubOk]           = useState('');
  const [editOpen, setEditOpen]     = useState(false);
  const [mergeOpen, setMergeOpen]   = useState(false);
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const imageRef = useRef();

  const refresh = useCallback(() => {
    if (!partyId) return;
    setLoading(true);
    ssPartyAPI.getFull(partyId)
      .then(r => setDetail(r.data || r))
      .finally(() => setLoading(false));
  }, [partyId]);

  useEffect(() => { refresh(); setTab('overview'); }, [refresh]);

  const loadAudit = useCallback(() => {
    setAuditLoading(true);
    ssPartyAPI.auditHistory(partyId)
      .then(r => setAuditEvents(r.data || r || []))
      .catch(() => {})
      .finally(() => setAuditLoading(false));
  }, [partyId]);

  useEffect(() => { if (tab === 'audit') loadAudit(); }, [tab, loadAudit]);

  const clearMsg = () => { setSubErr(''); setSubOk(''); };
  const p = detail?.party;

  if (loading || !detail) return <div style={{ padding: 40 }}><Spinner /></div>;

  // ── Sub-entity actions ─────────────────────────────────────────────────────
  const wrap = async (fn, successMsg) => {
    setSubSaving(true); clearMsg();
    try { await fn(); if (successMsg) setSubOk(successMsg); refresh(); }
    catch (ex) { setSubErr(fmtErr(ex.response?.data, 'Operation failed')); }
    finally { setSubSaving(false); }
  };

  // ── Tabs ───────────────────────────────────────────────────────────────────
  const TABS = [
    { id: 'overview',    label: 'Overview' },
    { id: 'addresses',   label: `Addresses (${detail.addresses?.length || 0})` },
    { id: 'contacts',    label: `Contacts (${detail.contacts?.length || 0})` },
    { id: 'identifiers', label: `IDs (${detail.identifiers?.length || 0})` },
    { id: 'roles',       label: `Roles (${detail.business_roles?.length || 0})` },
    { id: 'rels',        label: `Relations (${(detail.relationships_out?.length || 0) + (detail.relationships_in?.length || 0)})` },
    { id: 'documents',   label: 'Documents' },
    { id: 'image',       label: 'Image' },
    { id: 'customdata',  label: 'Custom Data' },
    { id: 'audit',       label: 'Audit' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${COLOR.grayBorder}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: COLOR.text }}>{p?.display_name || p?.name || `Party #${partyId}`}</div>
          <div style={{ fontSize: 12, color: COLOR.textMuted, marginTop: 3 }}>ID #{partyId} · {p?.type === 'PER' ? 'Person' : 'Organization'}</div>
          <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <ActiveBadge active={p?.is_active} />
            <PartyTypePill type={p?.type} />
            {(detail.business_roles || []).map(r => <Badge key={r.code} color="purple">{r.code}</Badge>)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Button size="sm" onClick={() => setEditOpen(true)}>✏️ Edit</Button>
          <Button variant="ghost" size="sm" onClick={() => setMergeOpen(true)}>🔀 Merge</Button>
          {/* Unmerge — only meaningful while at least one merge for this
              survivor is still inside the 30-day eligibility window. The
              button is always rendered; clicking opens a modal that filters
              the merge log to this survivor and shows the countdown. */}
          <UnmergeButton survivorPartyId={partyId} onDone={() => { refresh(); onRefresh?.(); }} />

          {/* Deactivate / Restore — prominent contextual toggle */}
          {p?.is_active
            ? (
              <Button variant="ghostRed" size="sm"
                onClick={() => wrap(() => ssPartyAPI.close(partyId, 'Deactivated via MIS'), 'Party deactivated')}
                title="Deactivate this party (soft-delete)">
                🚫 Deactivate
              </Button>
            ) : (
              <Button variant="success" size="sm"
                onClick={() => wrap(() => ssPartyAPI.restore(partyId), 'Party restored')}
                title="Reactivate this party">
                ✅ Restore
              </Button>
            )
          }

          {/* ⋯ Overflow menu — Export JSON / Copy permalink */}
          <OverflowMenu partyId={partyId} party={p} />

          <Button variant="ghost" size="sm" onClick={onClose} title="Close detail panel">✕</Button>
        </div>
      </div>

      <TabBar tabs={TABS} active={tab} onChange={t => { clearMsg(); setTab(t); }} style={{ paddingLeft: 20, flexShrink: 0 }} />

      {/* Tab content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {subErr && <ErrorBanner>{subErr}</ErrorBanner>}
        {subOk  && <SuccessBanner>{subOk}</SuccessBanner>}
{/* ── Overview ──────────────────────────────────────────────────────── */}
{tab === 'overview' && (
  <>
    <InfoGrid
      rows={[
        ['Party ID', p?.partyid],
        ['Type', p?.type === 'PER' ? 'Person' : 'Organization'],
        ...(p?.type === 'PER'
          ? [
              ['First Name', p?.firstname],
              ['Middle Name', p?.middlename],
              ['Last Name', p?.lastname],
              [
                'Gender',
                p?.gender === 'M'
                  ? 'Male'
                  : p?.gender === 'F'
                  ? 'Female'
                  : p?.gender,
              ],
              [
                'Birth Date',
                p?.birthdate ? p.birthdate.split('T')[0] : null,
              ],
            ]
          : [['Org Name', p?.name]]),
        ['Notes', p?.notes],
        [
          'Owner Company',
          p?.ownercompany ? `Company #${p.ownercompany}` : null,
        ],
        ['Active From', p?.datefrom],
        ['Closed At', p?.dateto],
        [
          'User Account',
          detail.user_account
            ? `${detail.user_account.username} (ID #${detail.user_account.userid})`
            : 'None',
        ],
      ]}
    />

    {/* ── Attachments (ssentitiesattachedinfo) ── */}
    {detail.attachments && detail.attachments.length > 0 && (
      <Section
        title={`Attachments (${detail.attachments.length})`}
        style={{ marginTop: 16 }}
      >
        {detail.attachments.map((a) => (
          <div
            key={a.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 10px',
              border: `1px solid ${COLOR.grayBorder}`,
              borderRadius: RADIUS.md,
              marginBottom: 6,
              background: '#fff',
            }}
          >
            <span style={{ fontSize: 18 }}>📎</span>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 13,
                  color: COLOR.text,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {a.filename || 'Untitled'}
              </div>

              <div
                style={{
                  fontSize: 11,
                  color: COLOR.textMuted,
                  marginTop: 2,
                }}
              >
                {[
                  a.contenttype,
                  a.filesize ? `${(a.filesize / 1024).toFixed(1)} KB` : null,
                  a.uploadedat
                    ? new Date(a.uploadedat).toLocaleDateString()
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>

              {a.description && (
                <div
                  style={{
                    fontSize: 12,
                    color: COLOR.textMuted,
                    marginTop: 2,
                  }}
                >
                  {a.description}
                </div>
              )}
            </div>

            {a.blobref && (
              <a
                href={a.blobref}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: 12,
                  color: COLOR.blue,
                  textDecoration: 'none',
                  flexShrink: 0,
                  fontWeight: 600,
                }}
              >
                Download
              </a>
            )}
          </div>
        ))}
      </Section>
    )}
  </>
)}
        {/* ── Addresses ─────────────────────────────────────────────────────── */}
        {tab === 'addresses' && (
          <AddressTab partyId={partyId} addresses={detail.addresses || []} addrTypes={lookups.addrTypes}
            wrap={wrap} subSaving={subSaving} onRefresh={refresh} />
        )}

        {/* ── Contacts ──────────────────────────────────────────────────────── */}
        {tab === 'contacts' && (
          <ContactTab partyId={partyId} contacts={detail.contacts || []} contactMedia={lookups.contactMedia}
            contactMediaTypes={lookups.contactMediaTypes} wrap={wrap} subSaving={subSaving} />
        )}

        {/* ── Identifiers ───────────────────────────────────────────────────── */}
        {tab === 'identifiers' && (
          <IdentifierTab partyId={partyId} identifiers={detail.identifiers || []} identTypes={lookups.identTypes}
            partyType={p?.type} wrap={wrap} subSaving={subSaving} />
        )}

        {/* ── Business Roles ────────────────────────────────────────────────── */}
        {tab === 'roles' && (
          <RolesTab partyId={partyId} roles={detail.business_roles || []} partyRoles={lookups.partyRoles}
            wrap={wrap} subSaving={subSaving} />
        )}

        {/* ── Relationships ─────────────────────────────────────────────────── */}
        {tab === 'rels' && (
          <RelationshipsTab partyId={partyId} out={detail.relationships_out || []} inn={detail.relationships_in || []}
            relTypes={lookups.relTypes} wrap={wrap} subSaving={subSaving} onRefresh={refresh} />
        )}

        {/* ── Documents ─────────────────────────────────────────────────────── */}
        {tab === 'documents' && (
          <SSDocumentsTab partyId={partyId} />
        )}

        {/* ── Image ─────────────────────────────────────────────────────────── */}
        {tab === 'image' && (
          <ImageTab partyId={partyId} imageRef={imageRef} wrap={wrap} subSaving={subSaving} />
        )}

        {/* ── Custom Data ───────────────────────────────────────────────────── */}
        {tab === 'customdata' && (
          <CustomDataTab partyId={partyId} rawJson={p?.customdata} wrap={wrap} subSaving={subSaving} />
        )}

        {/* ── Audit History ─────────────────────────────────────────────────── */}
        {/* Upgraded from flat DataTable to SSAuditTimeline (grouped by day,
            colour-coded by action, diff expansion). */}
        {tab === 'audit' && (
          <SSAuditTimeline partyId={partyId} />
        )}
      </div>

      {/* Modals */}
      <PartyFormModal open={editOpen} initial={p} lookups={lookups} onClose={() => setEditOpen(false)} onDone={() => { setEditOpen(false); refresh(); onRefresh?.(); }} />
      <MergeModal partyId={partyId} open={mergeOpen} onClose={() => setMergeOpen(false)} onDone={onRefresh} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Country / State / City data — used by the Address form for cascading dropdowns.
// `null` for state/city means "no curated list — fall back to a free-text input".
// ─────────────────────────────────────────────────────────────────────────────
const COUNTRY_DATA = {
  // ── East / Sub-Saharan Africa ───────────────────────────────────────────────
  'Kenya': {
    states: {
      'Nairobi':         ['Nairobi', 'Westlands', 'Karen', 'Kilimani', 'Embakasi', 'Kasarani', 'Langata', 'Ruaka', 'Ruiru'],
      'Mombasa':         ['Mombasa', 'Nyali', 'Likoni', 'Bamburi', 'Kisauni', 'Mtwapa'],
      'Kisumu':          ['Kisumu', 'Ahero', 'Maseno', 'Muhoroni'],
      'Nakuru':          ['Nakuru', 'Naivasha', 'Gilgil', 'Molo', 'Njoro'],
      'Uasin Gishu':     ['Eldoret', 'Burnt Forest', 'Turbo'],
      'Kiambu':          ['Thika', 'Ruiru', 'Kikuyu', 'Limuru', 'Juja', 'Kiambu Town'],
      'Machakos':        ['Machakos', 'Athi River', 'Mlolongo', 'Kangundo'],
      'Kajiado':         ['Kitengela', 'Ngong', 'Kajiado', 'Ongata Rongai', 'Kiserian'],
      'Kilifi':          ['Kilifi', 'Malindi', 'Watamu', 'Mtwapa'],
      'Kwale':           ['Kwale', 'Diani', 'Ukunda', 'Lunga Lunga'],
      'Kakamega':        ['Kakamega', 'Mumias', 'Malava', 'Lugari'],
      'Bungoma':         ['Bungoma', 'Webuye', 'Kimilili'],
      'Trans Nzoia':     ['Kitale', 'Kiminini'],
      'Garissa':         ['Garissa', 'Dadaab'],
      'Wajir':           ['Wajir'],
      'Mandera':         ['Mandera'],
      'Marsabit':        ['Marsabit', 'Moyale'],
      'Isiolo':          ['Isiolo'],
      'Meru':            ['Meru', 'Maua', 'Nkubu'],
      'Nyeri':           ['Nyeri', 'Karatina', 'Othaya'],
      'Murang\'a':       ['Murang\'a', 'Maragua', 'Kangema'],
      'Kirinyaga':       ['Kerugoya', 'Mwea', 'Sagana'],
      'Embu':            ['Embu', 'Runyenjes'],
      'Kitui':           ['Kitui', 'Mwingi'],
      'Makueni':         ['Wote', 'Makueni', 'Mtito Andei'],
      'Nyandarua':       ['Ol Kalou', 'Nyahururu'],
      'Laikipia':        ['Nanyuki', 'Nyahururu', 'Rumuruti'],
      'Narok':           ['Narok', 'Kilgoris', 'Mara'],
      'Kericho':         ['Kericho', 'Litein'],
      'Bomet':           ['Bomet', 'Sotik'],
      'Nandi':           ['Kapsabet', 'Nandi Hills'],
      'Baringo':         ['Kabarnet', 'Eldama Ravine'],
      'Elgeyo-Marakwet': ['Iten', 'Kapsowar'],
      'West Pokot':      ['Kapenguria'],
      'Samburu':         ['Maralal'],
      'Turkana':         ['Lodwar', 'Lokichoggio', 'Kakuma'],
      'Tana River':      ['Hola', 'Garsen'],
      'Lamu':            ['Lamu', 'Mokowe'],
      'Taita-Taveta':    ['Voi', 'Wundanyi', 'Taveta'],
      'Tharaka-Nithi':   ['Chuka', 'Marimanti'],
      'Vihiga':          ['Vihiga', 'Mbale'],
      'Busia':           ['Busia', 'Malaba'],
      'Siaya':           ['Siaya', 'Bondo', 'Yala'],
      'Homa Bay':        ['Homa Bay', 'Mbita', 'Oyugis'],
      'Migori':          ['Migori', 'Awendo', 'Rongo'],
      'Kisii':           ['Kisii', 'Ogembo'],
      'Nyamira':         ['Nyamira', 'Keroka'],
    },
  },
  'Uganda':       { states: { 'Central': ['Kampala', 'Entebbe', 'Mukono', 'Wakiso'], 'Eastern': ['Jinja', 'Mbale', 'Soroti'], 'Northern': ['Gulu', 'Lira'], 'Western': ['Mbarara', 'Fort Portal', 'Kasese'] } },
  'Tanzania':     { states: { 'Dar es Salaam': ['Dar es Salaam', 'Kigamboni'], 'Arusha': ['Arusha'], 'Mwanza': ['Mwanza'], 'Dodoma': ['Dodoma'], 'Mbeya': ['Mbeya'], 'Zanzibar Urban/West': ['Zanzibar City', 'Stone Town'] } },
  'Rwanda':       { states: { 'Kigali': ['Kigali', 'Nyarugenge', 'Kicukiro', 'Gasabo'], 'Eastern Province': ['Rwamagana', 'Kayonza'], 'Northern Province': ['Musanze', 'Byumba'], 'Southern Province': ['Huye', 'Nyanza'], 'Western Province': ['Rubavu', 'Karongi'] } },
  'Ethiopia':     { states: { 'Addis Ababa': ['Addis Ababa'], 'Oromia': ['Adama', 'Jimma', 'Bishoftu'], 'Amhara': ['Bahir Dar', 'Gondar'], 'Tigray': ['Mekelle'], 'Sidama': ['Hawassa'] } },
  'Nigeria':      { states: { 'Lagos': ['Ikeja', 'Lagos Island', 'Lekki', 'Surulere'], 'Abuja FCT': ['Abuja', 'Garki', 'Wuse'], 'Rivers': ['Port Harcourt'], 'Kano': ['Kano'], 'Oyo': ['Ibadan'], 'Kaduna': ['Kaduna'], 'Anambra': ['Awka', 'Onitsha'], 'Enugu': ['Enugu'] } },
  'Ghana':        { states: { 'Greater Accra': ['Accra', 'Tema'], 'Ashanti': ['Kumasi', 'Obuasi'], 'Western': ['Takoradi', 'Sekondi'], 'Central': ['Cape Coast'], 'Northern': ['Tamale'] } },
  'South Africa': { states: { 'Gauteng': ['Johannesburg', 'Pretoria', 'Sandton', 'Soweto'], 'Western Cape': ['Cape Town', 'Stellenbosch'], 'KwaZulu-Natal': ['Durban', 'Pietermaritzburg'], 'Eastern Cape': ['Port Elizabeth', 'East London'], 'Free State': ['Bloemfontein'] } },
  'Zambia':       { states: { 'Lusaka': ['Lusaka'], 'Copperbelt': ['Ndola', 'Kitwe'], 'Southern': ['Livingstone'] } },
  'Zimbabwe':     { states: { 'Harare': ['Harare'], 'Bulawayo': ['Bulawayo'], 'Manicaland': ['Mutare'] } },
  'Botswana':     { states: null },
  'Mozambique':   { states: null },
  "Cote d'Ivoire":{ states: null },
  'Cameroon':     { states: null },
  'Senegal':      { states: null },
  'Malawi':       { states: null },
  'Namibia':      { states: null },
  'Somalia':      { states: null },
  'Sudan':        { states: null },
  'South Sudan':  { states: null },
  'DR Congo':     { states: null },
  'Angola':       { states: null },

  // ── Major international (cascading state lists) ─────────────────────────────
  'United Kingdom': { states: { 'England': ['London', 'Manchester', 'Birmingham', 'Leeds', 'Liverpool', 'Bristol', 'Sheffield', 'Newcastle', 'Nottingham', 'Leicester'], 'Scotland': ['Edinburgh', 'Glasgow', 'Aberdeen', 'Dundee'], 'Wales': ['Cardiff', 'Swansea', 'Newport'], 'Northern Ireland': ['Belfast', 'Derry'] } },
  'United States':  { states: { 'California': ['Los Angeles', 'San Francisco', 'San Diego', 'San Jose', 'Sacramento'], 'New York': ['New York City', 'Buffalo', 'Albany', 'Rochester'], 'Texas': ['Houston', 'Dallas', 'Austin', 'San Antonio'], 'Florida': ['Miami', 'Orlando', 'Tampa', 'Jacksonville'], 'Illinois': ['Chicago', 'Springfield'], 'Massachusetts': ['Boston', 'Cambridge', 'Worcester'], 'Washington': ['Seattle', 'Spokane', 'Tacoma'], 'Georgia': ['Atlanta', 'Savannah'], 'Pennsylvania': ['Philadelphia', 'Pittsburgh'], 'Arizona': ['Phoenix', 'Tucson'], 'New Jersey': ['Newark', 'Jersey City'], 'Virginia': ['Richmond', 'Norfolk', 'Arlington'], 'North Carolina': ['Charlotte', 'Raleigh'], 'Ohio': ['Columbus', 'Cleveland', 'Cincinnati'], 'Michigan': ['Detroit', 'Ann Arbor'], 'Colorado': ['Denver', 'Boulder'] } },
  'Canada':         { states: { 'Ontario': ['Toronto', 'Ottawa', 'Mississauga', 'Hamilton'], 'Quebec': ['Montreal', 'Quebec City', 'Laval'], 'British Columbia': ['Vancouver', 'Victoria', 'Burnaby'], 'Alberta': ['Calgary', 'Edmonton'], 'Manitoba': ['Winnipeg'], 'Saskatchewan': ['Saskatoon', 'Regina'], 'Nova Scotia': ['Halifax'] } },
  'Australia':      { states: { 'New South Wales': ['Sydney', 'Newcastle', 'Wollongong'], 'Victoria': ['Melbourne', 'Geelong'], 'Queensland': ['Brisbane', 'Gold Coast', 'Cairns'], 'Western Australia': ['Perth', 'Fremantle'], 'South Australia': ['Adelaide'], 'Tasmania': ['Hobart'], 'Australian Capital Territory': ['Canberra'] } },
  'India':          { states: { 'Maharashtra': ['Mumbai', 'Pune', 'Nagpur', 'Nashik'], 'Delhi': ['New Delhi', 'Delhi'], 'Karnataka': ['Bengaluru', 'Mysuru', 'Mangaluru'], 'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai'], 'Telangana': ['Hyderabad', 'Warangal'], 'West Bengal': ['Kolkata', 'Howrah'], 'Gujarat': ['Ahmedabad', 'Surat', 'Vadodara'], 'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Varanasi', 'Noida'], 'Rajasthan': ['Jaipur', 'Udaipur', 'Jodhpur'], 'Kerala': ['Kochi', 'Thiruvananthapuram', 'Kozhikode'], 'Punjab': ['Chandigarh', 'Ludhiana', 'Amritsar'], 'Haryana': ['Gurugram', 'Faridabad'] } },
  'China':          { states: null },
  'Germany':        { states: { 'Bavaria': ['Munich', 'Nuremberg'], 'Berlin': ['Berlin'], 'Hamburg': ['Hamburg'], 'North Rhine-Westphalia': ['Cologne', 'Düsseldorf', 'Dortmund'], 'Hesse': ['Frankfurt'], 'Baden-Württemberg': ['Stuttgart', 'Karlsruhe'] } },
  'France':         { states: { 'Île-de-France': ['Paris', 'Versailles'], 'Provence-Alpes-Côte d\'Azur': ['Marseille', 'Nice'], 'Auvergne-Rhône-Alpes': ['Lyon', 'Grenoble'], 'Occitanie': ['Toulouse', 'Montpellier'], 'Hauts-de-France': ['Lille'] } },
  'Netherlands':    { states: { 'North Holland': ['Amsterdam', 'Haarlem'], 'South Holland': ['Rotterdam', 'The Hague', 'Leiden'], 'Utrecht': ['Utrecht'], 'North Brabant': ['Eindhoven', 'Tilburg'] } },
  'Sweden':         { states: null },
  'Norway':         { states: null },
  'Denmark':        { states: null },
  'Italy':          { states: { 'Lazio': ['Rome'], 'Lombardy': ['Milan', 'Bergamo'], 'Campania': ['Naples'], 'Veneto': ['Venice', 'Verona'], 'Tuscany': ['Florence', 'Pisa'], 'Sicily': ['Palermo', 'Catania'] } },
  'Spain':          { states: { 'Madrid': ['Madrid'], 'Catalonia': ['Barcelona', 'Tarragona'], 'Andalusia': ['Seville', 'Málaga', 'Granada'], 'Valencia': ['Valencia', 'Alicante'] } },
  'United Arab Emirates': { states: { 'Dubai': ['Dubai'], 'Abu Dhabi': ['Abu Dhabi', 'Al Ain'], 'Sharjah': ['Sharjah'], 'Ajman': ['Ajman'], 'Ras Al Khaimah': ['Ras Al Khaimah'], 'Fujairah': ['Fujairah'], 'Umm Al Quwain': ['Umm Al Quwain'] } },
  'Saudi Arabia':   { states: { 'Riyadh': ['Riyadh'], 'Makkah': ['Mecca', 'Jeddah', 'Taif'], 'Madinah': ['Medina'], 'Eastern Province': ['Dammam', 'Khobar', 'Dhahran'] } },
  'Qatar':          { states: null },
  'Kuwait':         { states: null },
  'Bahrain':        { states: null },
  'Oman':           { states: null },
};

// Country names sorted: African block first (alphabetical), then divider, then global.
const COUNTRIES_AFRICA = [
  'Kenya','Uganda','Tanzania','Rwanda','Ethiopia','Nigeria','Ghana','South Africa',
  'Zambia','Zimbabwe','Botswana','Mozambique',"Cote d'Ivoire",'Cameroon','Senegal',
  'Malawi','Namibia','Somalia','Sudan','South Sudan','DR Congo','Angola',
];
const COUNTRIES_GLOBAL = [
  'United Kingdom','United States','Canada','Australia','India','China',
  'Germany','France','Netherlands','Sweden','Norway','Denmark','Italy','Spain',
  'United Arab Emirates','Saudi Arabia','Qatar','Kuwait','Bahrain','Oman',
];
const COUNTRIES = [...COUNTRIES_AFRICA, '────────────────────', ...COUNTRIES_GLOBAL];

// Sentinel value used by the cascading address selects when the user
// chooses "Other (specify)" — surfaces a free-text input fallback.
const OTHER_SENTINEL = '__other__';

// ─────────────────────────────────────────────────────────────────────────────
// LocationFields — cascading Country / State / City / Postal selects.
// Renders a 2x2 (or 1x4 within parent grid) of fields that gracefully fall
// back to text inputs when the chosen country/state has no curated list.
// ─────────────────────────────────────────────────────────────────────────────
function LocationFields({ value, onChange, gap = 8, layout = 'grid' }) {
  const country  = value.country  || '';
  const state    = value.state    || '';
  const city     = value.city     || '';
  const postal   = value.postalcode || '';

  const countryData = COUNTRY_DATA[country];
  const states      = countryData?.states || null;
  const stateNames  = states ? Object.keys(states).sort((a, b) => a.localeCompare(b)) : null;
  const cityNames   = states && state && Array.isArray(states[state]) ? states[state] : null;

  // Track whether the user explicitly chose "Other" so we don't lose their
  // free-text value when the curated list rerenders.
  const stateIsCustom = state && stateNames && !stateNames.includes(state);
  const cityIsCustom  = city  && cityNames  && !cityNames.includes(city);

  // ── Change handlers — reset downstream fields when parent changes ──────────
  const setCountry = (e) => {
    const v = e.target.value;
    onChange({ country: v, state: '', city: '', postalcode: postal });
  };
  const setState = (e) => {
    const v = e.target.value === OTHER_SENTINEL ? '' : e.target.value;
    onChange({ country, state: v, city: '', postalcode: postal });
  };
  const setStateText = (e) => {
    onChange({ country, state: e.target.value, city, postalcode: postal });
  };
  const setCity = (e) => {
    const v = e.target.value === OTHER_SENTINEL ? '' : e.target.value;
    onChange({ country, state, city: v, postalcode: postal });
  };
  const setCityText = (e) => {
    onChange({ country, state, city: e.target.value, postalcode: postal });
  };
  const setPostal = (e) => {
    onChange({ country, state, city, postalcode: e.target.value });
  };

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: layout === 'inline' ? '1fr 1fr 1fr 1fr' : '1fr 1fr',
      gap,
    }}>
      {/* Country */}
      <Field label="Country" style={{ marginBottom: 0 }}>
        <Select value={country} onChange={setCountry}>
          <option value="">— select country —</option>
          {/* Surface legacy / free-text values so they don't appear blank */}
          {country && !COUNTRIES.includes(country) && (
            <option value={country}>{country} (custom)</option>
          )}
          {COUNTRIES.map((c, i) => (
            <option key={i} value={c.startsWith('───') ? '' : c} disabled={c.startsWith('───')}>
              {c}
            </option>
          ))}
        </Select>
      </Field>

      {/* State / Province / County — select if curated, else text */}
      <Field label="State / County" style={{ marginBottom: 0 }}>
        {stateNames ? (
          <>
            <Select
              value={stateIsCustom ? OTHER_SENTINEL : state}
              onChange={setState}
              disabled={!country}
            >
              <option value="">{country ? '— select —' : '— pick country first —'}</option>
              {stateNames.map(s => <option key={s} value={s}>{s}</option>)}
              <option value={OTHER_SENTINEL}>Other (specify)…</option>
            </Select>
            {stateIsCustom && (
              <Input
                value={state}
                onChange={setStateText}
                placeholder="Enter state / county"
                style={{ marginTop: 6 }}
              />
            )}
          </>
        ) : (
          <Input
            value={state}
            onChange={setStateText}
            placeholder={country ? 'Enter state / province' : '— pick country first —'}
            disabled={!country}
          />
        )}
      </Field>

      {/* City / Town — select if curated, else text */}
      <Field label="City / Town" style={{ marginBottom: 0 }}>
        {cityNames ? (
          <>
            <Select
              value={cityIsCustom ? OTHER_SENTINEL : city}
              onChange={setCity}
            >
              <option value="">— select city —</option>
              {cityNames.map(c => <option key={c} value={c}>{c}</option>)}
              <option value={OTHER_SENTINEL}>Other (specify)…</option>
            </Select>
            {cityIsCustom && (
              <Input
                value={city}
                onChange={setCityText}
                placeholder="Enter city / town"
                style={{ marginTop: 6 }}
              />
            )}
          </>
        ) : (
          <Input
            value={city}
            onChange={setCityText}
            placeholder={state || country ? 'Enter city / town' : '— pick country first —'}
            disabled={!country}
          />
        )}
      </Field>

      {/* Postal */}
      <Field label="Postal / ZIP" style={{ marginBottom: 0 }}>
        <Input value={postal} onChange={setPostal} placeholder="00100" />
      </Field>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared address form (add + edit reuse same layout)
// ─────────────────────────────────────────────────────────────────────────────
function AddrForm({ form, setForm, addrTypes }) {
  const upd = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
      <Field label="Address Type" style={{ marginBottom: 8 }}>
        <Select value={form.addresstypeid || ''} onChange={upd('addresstypeid')}>
          <option value="">— select type —</option>
          {addrTypes.map(t => (
            <option key={t.addresstypeid || t.id} value={t.addresstypeid || t.id}>{t.name}</option>
          ))}
        </Select>
      </Field>
      <Field label=" " style={{ marginBottom: 8, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, paddingBottom: 9 }}>
          <input type="checkbox" checked={!!form.ismain}
            onChange={e => setForm(f => ({ ...f, ismain: e.target.checked }))} />
          Main address
        </label>
      </Field>
      <Field label="Address Line 1" style={{ gridColumn: '1/-1', marginBottom: 8 }}>
        <Input value={form.address1 || ''} onChange={upd('address1')} placeholder="Street / building" />
      </Field>
      <Field label="Address Line 2" style={{ gridColumn: '1/-1', marginBottom: 8 }}>
        <Input value={form.address2 || ''} onChange={upd('address2')} placeholder="Apt, suite, floor, PO Box (optional)" />
      </Field>
      <div style={{ gridColumn: '1/-1' }}>
        <LocationFields
          value={{
            country:    form.country    || '',
            state:      form.state      || '',
            city:       form.city       || '',
            postalcode: form.postalcode || '',
          }}
          onChange={(next) => setForm(f => ({ ...f, ...next }))}
        />
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Address Tab
// ─────────────────────────────────────────────────────────────────────────────
function AddressTab({
  partyId,
  addresses,
  addrTypes,
  wrap,
  subSaving,
  onRefresh,
}) {
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [addForm, setAddForm] = useState(BLANK_ADDR);
  const [showAdd, setShowAdd] = useState(false);

  const startEdit = (a) => {
    setEditId(a.partyaddressid);
    setEditForm({
      addresstypeid: a.addresstypeid || "",
      address1: a.address1 || "",
      address2: a.address2 || "",
      city: a.city || "",
      state: a.state || "",
      postalcode: a.postalcode || "",
      country: a.country || "",
      ismain: !!a.ismain,
    });
  };

  const addrTypeName = (id) => {
    const t = addrTypes.find(
      (t) => String(t.addresstypeid || t.id) === String(id)
    );
    return t ? t.name : null;
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 12,
        }}
      >
        <Button size="sm" onClick={() => setShowAdd((s) => !s)}>
          {showAdd ? "Cancel" : "+ Add Address"}
        </Button>
      </div>

      {showAdd && (
        <Card style={{ marginBottom: 14, padding: 14 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              marginBottom: 10,
              color: COLOR.text,
            }}
          >
            New Address
          </div>

          <AddrForm
            form={addForm}
            setForm={setAddForm}
            addrTypes={addrTypes}
          />

          <Button
            size="sm"
            style={{ marginTop: 10 }}
            disabled={subSaving}
            onClick={() =>
              wrap(
                () =>
                  ssPartyAPI
                    .addAddress(partyId, {
                      ...addForm,
                      addresstypeid:
                        Number(addForm.addresstypeid) || undefined,
                    })
                    .then(() => {
                      setAddForm(BLANK_ADDR);
                      setShowAdd(false);
                    }),
                "Address added"
              )
            }
          >
            {subSaving ? "Saving…" : "Add Address"}
          </Button>
        </Card>
      )}

      {!showAdd && addresses.length === 0 && (
        <EmptyState
          icon="📍"
          title="No addresses"
          message="Add a home, work, or postal address."
        />
      )}

      {addresses.map((a) => (
        <Card
          key={a.partyaddressid}
          style={{ marginBottom: 10, padding: 14 }}
        >
          {editId === a.partyaddressid ? (
            <div>
              <AddrForm
                form={editForm}
                setForm={setEditForm}
                addrTypes={addrTypes}
              />

              <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                <Button
                  size="sm"
                  disabled={subSaving}
                  onClick={() =>
                    wrap(
                      () =>
                        ssPartyAPI
                          .updateAddress(
                            partyId,
                            a.partyaddressid,
                            {
                              ...editForm,
                              addresstypeid:
                                Number(editForm.addresstypeid) ||
                                undefined,
                            }
                          )
                          .then(() => setEditId(null)),
                      "Address updated"
                    )
                  }
                >
                  Save
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditId(null)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <div>
                {a.addresstypeid && (
                  <Badge color="teal" style={{ marginBottom: 5 }}>
                    {addrTypeName(a.addresstypeid) ||
                      a.address_type ||
                      "Address"}
                  </Badge>
                )}

                <div style={{ fontWeight: 600, fontSize: 13 }}>
                  {[a.address1, a.address2].filter(Boolean).join(", ")}
                </div>

                <div
                  style={{
                    fontSize: 12,
                    color: COLOR.textMuted,
                    marginTop: 2,
                  }}
                >
                  {[a.city, a.state, a.postalcode, a.country]
                    .filter(Boolean)
                    .join(", ")}
                </div>

                {a.ismain && (
                  <Badge color="green" style={{ marginTop: 5 }}>
                    Main
                  </Badge>
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 5,
                  flexShrink: 0,
                  marginLeft: 8,
                }}
              >
                {!a.ismain && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      wrap(
                        () =>
                          ssPartyAPI.setMainAddress(
                            partyId,
                            a.partyaddressid
                          ),
                        "Set as main"
                      )
                    }
                  >
                    Set Main
                  </Button>
                )}

                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => startEdit(a)}
                >
                  Edit
                </Button>

                <Button
                  size="sm"
                  variant="danger"
                  onClick={() =>
                    wrap(
                      () =>
                        ssPartyAPI.removeAddress(
                          partyId,
                          a.partyaddressid
                        ),
                      "Removed"
                    )
                  }
                >
                  ✕
                </Button>
              </div>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Contact Tab — channel + subtype dropdowns, verify badge
// ─────────────────────────────────────────────────────────────────────────────
// ── ContactForm — top-level so React never remounts it on parent re-render ────
const CHAN_ICONS_MAP = { EMAIL:"✉️", PHONE:"📞", MOBILE:"📱", WHATSAPP:"💬", FAX:"📠", WEBSITE:"🌐" };
function ContactForm({ form, setForm, contactMedia, contactMediaTypes }) {
  const subtypes = (mediaId) =>
    contactMediaTypes.filter((t) => String(t.contactmediaid) === String(mediaId));
  const chanIcon = (code) => CHAN_ICONS_MAP[String(code).toUpperCase()] || "📋";
  const hasSubs = subtypes(form.contactmediaid).length > 0;
  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
      <Field label="Channel" style={{ marginBottom:0 }}>
        <Select value={form.contactmediaid || ""} onChange={(e) =>
          setForm((f) => ({ ...f, contactmediaid: e.target.value, contactmediatypeid: "" }))}>
          <option value="">— select channel —</option>
          {contactMedia.map((m) => (
            <option key={m.contactmediaid || m.id} value={m.contactmediaid || m.id}>
              {chanIcon(m.code)} {m.name}
            </option>
          ))}
        </Select>
      </Field>

      {hasSubs && (
        <Field label="Sub-type" style={{ marginBottom:0 }}>
          <Select value={form.contactmediatypeid || ""}
            onChange={(e) => setForm((f) => ({ ...f, contactmediatypeid: e.target.value }))}>
            <option value="">— any —</option>
            {subtypes(form.contactmediaid).map((t) => (
              <option key={t.contactmediatypeid || t.id} value={t.contactmediatypeid || t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </Field>
      )}

      <Field label="Value" style={{ gridColumn: hasSubs ? "1/-1" : undefined, marginBottom:0 }}>
        <Input value={form.value || ""}
          onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
          placeholder="phone number / email / handle" />
      </Field>

      <Field label=" " style={{ marginBottom:0, display:"flex", alignItems:"flex-end" }}>
        <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, paddingBottom:9 }}>
          <input type="checkbox" checked={!!form.isprimary}
            onChange={(e) => setForm((f) => ({ ...f, isprimary: e.target.checked }))} />
          Primary contact
        </label>
      </Field>
    </div>
  );
}

function ContactTab({
  partyId,
  contacts,
  contactMedia,
  contactMediaTypes,
  wrap,
  subSaving,
}) {
  const [addForm, setAddForm] = useState(BLANK_CONTACT);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const chanName = (id) => {
    const m = contactMedia.find((m) => String(m.contactmediaid || m.id) === String(id));
    return m ? m.name : String(id);
  };

  const subtypeName = (id) => {
    const t = contactMediaTypes.find((t) => String(t.contactmediatypeid || t.id) === String(id));
    return t ? t.name : null;
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 12,
        }}
      >
        <Button size="sm" onClick={() => setShowAdd((s) => !s)}>
          {showAdd ? "Cancel" : "+ Add Contact"}
        </Button>
      </div>

      {showAdd && (
        <Card style={{ marginBottom: 14, padding: 14 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              marginBottom: 10,
              color: COLOR.text,
            }}
          >
            New Contact
          </div>

          <ContactForm form={addForm} setForm={setAddForm} contactMedia={contactMedia} contactMediaTypes={contactMediaTypes} />

          <Button
            size="sm"
            disabled={subSaving}
            onClick={() =>
              wrap(
                () =>
                  ssPartyAPI
                    .addContact(partyId, {
                      ...addForm,
                      contactmediaid:
                        Number(addForm.contactmediaid) || undefined,
                      contactmediatypeid:
                        Number(addForm.contactmediatypeid) || undefined,
                    })
                    .then(() => {
                      setAddForm(BLANK_CONTACT);
                      setShowAdd(false);
                    }),
                "Contact added"
              )
            }
          >
            {subSaving ? "Saving…" : "Add Contact"}
          </Button>
        </Card>
      )}

      {!showAdd && contacts.length === 0 && (
        <EmptyState
          icon="📞"
          title="No contacts"
          message="Add a phone, email, or other channel."
        />
      )}

      {contacts.map((c) => (
        <Card
          key={c.partycontactid}
          style={{ marginBottom: 8, padding: 12 }}
        >
          {editId === c.partycontactid ? (
            <div>
              <ContactForm form={editForm} setForm={setEditForm} contactMedia={contactMedia} contactMediaTypes={contactMediaTypes} />

              <div style={{ display: "flex", gap: 6 }}>
                <Button
                  size="sm"
                  disabled={subSaving}
                  onClick={() =>
                    wrap(
                      () =>
                        ssPartyAPI
                          .updateContact(partyId, c.partycontactid, {
                            ...editForm,
                            contactmediaid:
                              Number(editForm.contactmediaid) ||
                              undefined,
                            contactmediatypeid:
                              Number(editForm.contactmediatypeid) ||
                              undefined,
                          })
                          .then(() => setEditId(null)),
                      "Contact updated"
                    )
                  }
                >
                  Save
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditId(null)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: 6 }}
                >
                  <span style={{ fontSize: 16 }}>
                    {chanIcon(c.media_code || "")}
                  </span>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>
                    {c.value}
                  </span>
                  <Badge color="teal">
                    {chanName(c.contactmediaid)}
                  </Badge>
                  {c.contactmediatypeid &&
                    subtypeName(c.contactmediatypeid) && (
                      <Badge color="gray">
                        {subtypeName(c.contactmediatypeid)}
                      </Badge>
                    )}
                </div>

                <div style={{ display: "flex", gap: 4, marginTop: 5 }}>
                  {c.isprimary && <Badge color="green">Primary</Badge>}
                  {c.isverified && <Badge color="blue">Verified ✓</Badge>}
                </div>
              </div>

              <div style={{ display: "flex", gap: 5 }}>
                {!c.isverified && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      wrap(
                        () =>
                          ssPartyAPI.verifyContact(
                            partyId,
                            c.partycontactid,
                            ""
                          ),
                        "Verification sent"
                      )
                    }
                  >
                    Verify
                  </Button>
                )}

                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditId(c.partycontactid);
                    setEditForm({
                      contactmediaid: c.contactmediaid,
                      contactmediatypeid:
                        c.contactmediatypeid || "",
                      value: c.value,
                      isprimary: !!c.isprimary,
                    });
                  }}
                >
                  Edit
                </Button>

                <Button
                  size="sm"
                  variant="danger"
                  onClick={() =>
                    wrap(
                      () =>
                        ssPartyAPI.removeContact(
                          partyId,
                          c.partycontactid
                        ),
                      "Removed"
                    )
                  }
                >
                  ✕
                </Button>
              </div>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}


// ── IdentForm — top-level so React never remounts it on parent re-render ──────
function IdentForm({ form, setForm, eligible }) {
  const selType = eligible.find(
    (t) => String(t.identifiertypeid || t.id) === String(form.identifiertypeid)
  );
  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
      <Field label="Identifier Type" style={{ marginBottom:0 }}>
        <Select value={form.identifiertypeid || ""}
          onChange={(e) => setForm((f) => ({ ...f, identifiertypeid: e.target.value }))}>
          <option value="">— select type —</option>
          {eligible.map((t) => (
            <option key={t.identifiertypeid || t.id} value={t.identifiertypeid || t.id}>
              {t.name}
            </option>
          ))}
        </Select>
      </Field>

      <Field label="Value" style={{ marginBottom:0 }}>
        <Input value={form.value || ""}
          onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
          placeholder="ID number" />
      </Field>

      <Field label={selType?.requireissuanceauthority ? "Issuing Authority *" : "Issuing Authority"}
        style={{ gridColumn:"1/-1", marginBottom:0 }}>
        <Input value={form.issuanceauthority || ""}
          onChange={(e) => setForm((f) => ({ ...f, issuanceauthority: e.target.value }))}
          placeholder="e.g. Government of Kenya, NRSA…" />
      </Field>

      <Field label="Issue Date" style={{ marginBottom:0 }}>
        <Input type="date" value={form.issuancedate || ""}
          onChange={(e) => setForm((f) => ({ ...f, issuancedate: e.target.value }))} />
      </Field>

      <Field label="Expiry Date" style={{ marginBottom:0 }}>
        <Input type="date" value={form.expirationdate || ""}
          onChange={(e) => setForm((f) => ({ ...f, expirationdate: e.target.value }))} />
      </Field>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Identifier Tab — type-driven required fields + issuancedate
// ─────────────────────────────────────────────────────────────────────────────
function IdentifierTab({
  partyId,
  identifiers,
  identTypes,
  partyType,
  wrap,
  subSaving,
}) {
  const [addForm, setAddForm] = useState(BLANK_IDENT);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Filter types by party type
  const eligible = identTypes.filter(
    (t) =>
      !t.allowedpartytype ||
      t.allowedpartytype === partyType ||
      t.allowedpartytype === "PER+ORG"
  );

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 12,
        }}
      >
        <Button size="sm" onClick={() => setShowAdd((s) => !s)}>
          {showAdd ? "Cancel" : "+ Add Identifier"}
        </Button>
      </div>

      {showAdd && (
        <Card style={{ marginBottom: 14, padding: 14 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              marginBottom: 10,
              color: COLOR.text,
            }}
          >
            New Identifier
          </div>

          <IdentForm form={addForm} setForm={setAddForm} eligible={eligible} />

          <Button
            size="sm"
            disabled={subSaving}
            onClick={() =>
              wrap(
                () =>
                  ssPartyAPI
                    .addIdentifier(partyId, {
                      ...addForm,
                      identifiertypeid:
                        Number(addForm.identifiertypeid),
                    })
                    .then(() => {
                      setAddForm(BLANK_IDENT);
                      setShowAdd(false);
                    }),
                "Identifier added"
              )
            }
          >
            {subSaving ? "Saving…" : "Add Identifier"}
          </Button>
        </Card>
      )}

      {!showAdd && identifiers.length === 0 && (
        <EmptyState
          icon="🪪"
          title="No identifiers"
          message="Add a national ID, passport, or other document."
        />
      )}

      {identifiers.map((i) => (
        <Card
          key={i.partyidentifierid}
          style={{ marginBottom: 8, padding: 12 }}
        >
          {editId === i.partyidentifierid ? (
            <div>
              <IdentForm form={editForm} setForm={setEditForm} eligible={eligible} />

              <div style={{ display: "flex", gap: 6 }}>
                <Button
                  size="sm"
                  disabled={subSaving}
                  onClick={() =>
                    wrap(
                      () =>
                        ssPartyAPI
                          .updateIdentifier(
                            partyId,
                            i.partyidentifierid,
                            editForm
                          )
                          .then(() => setEditId(null)),
                      "Identifier updated"
                    )
                  }
                >
                  Save
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditId(null)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <div>
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontWeight: 700, fontSize: 14 }}>
                    {i.value}
                  </span>
                  <Badge color="amber">
                    {i.type_name || i.type_code || "ID"}
                  </Badge>
                  {i.ismain && (
                    <Badge color="green">Main</Badge>
                  )}
                </div>

                {i.issuanceauthority && (
                  <div
                    style={{
                      fontSize: 12,
                      color: COLOR.textMuted,
                      marginTop: 3,
                    }}
                  >
                    Issued by: {i.issuanceauthority}
                    {i.issuancedate && (
                      <span>
                        {" "}
                        · {i.issuancedate.split("T")[0]}
                      </span>
                    )}
                  </div>
                )}

                {i.expirationdate && (
                  <div
                    style={{
                      fontSize: 12,
                      color: COLOR.red,
                      marginTop: 2,
                    }}
                  >
                    Expires: {i.expirationdate.split("T")[0]}
                  </div>
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 5,
                  flexShrink: 0,
                  marginLeft: 8,
                }}
              >
                {!i.ismain && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      wrap(
                        () =>
                          ssPartyAPI.setMainIdentifier(
                            partyId,
                            i.partyidentifierid
                          ),
                        "Set as main"
                      )
                    }
                  >
                    Set Main
                  </Button>
                )}

                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditId(i.partyidentifierid);
                    setEditForm({
                      identifiertypeid: i.identifiertypeid,
                      value: i.value,
                      issuanceauthority:
                        i.issuanceauthority || "",
                      issuancedate:
                        i.issuancedate?.split("T")[0] || "",
                      expirationdate:
                        i.expirationdate?.split("T")[0] || "",
                    });
                  }}
                >
                  Edit
                </Button>

                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    if (
                      window.confirm(
                        "Delete identifier? This cannot be undone."
                      )
                    ) {
                      wrap(
                        () =>
                          ssPartyAPI.deleteIdentifier(
                            partyId,
                            i.partyidentifierid
                          ),
                        "Deleted"
                      );
                    }
                  }}
                >
                  ✕
                </Button>
              </div>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Roles Tab — assign / revoke business roles
// ─────────────────────────────────────────────────────────────────────────────
function RolesTab({ partyId, roles, partyRoles, wrap, subSaving }) {
  const [roleCode, setRoleCode] = useState("");

  return (
    <div>
      {roles.length === 0 && (
        <EmptyState icon="🏷️" title="No roles assigned" />
      )}

      {roles.map((r) => {
        const aid = r.assignment_id || r.partypartyroleid;

        return (
          <div
            key={aid || r.code}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 12px",
              borderRadius: RADIUS.md,
              border: `1px solid ${COLOR.grayBorder}`,
              marginBottom: 8,
              background: "#fff",
            }}
          >
            <div>
              <span style={{ fontWeight: 600, fontSize: 13 }}>
                {r.name}
              </span>
              <Badge color="purple" style={{ marginLeft: 8 }}>
                {r.code}
              </Badge>
            </div>

            <Button
              variant="ghostRed"
              size="sm"
              disabled={!aid}
              onClick={() =>
                wrap(
                  () =>
                    ssPartyAPI.revokePartyRole(partyId, aid),
                  "Role revoked"
                )
              }
            >
              Revoke
            </Button>
          </div>
        );
      })}

      <Section title="Assign Role" style={{ marginTop: 16 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <Select
            style={{ flex: 1 }}
            value={roleCode}
            onChange={(e) => setRoleCode(e.target.value)}
          >
            <option value="">— select role —</option>
            {partyRoles.map((r) => (
              <option key={r.code} value={r.code}>
                {r.name} ({r.code})
              </option>
            ))}
          </Select>

          <Button
            variant="success"
            size="sm"
            disabled={subSaving || !roleCode}
            onClick={() =>
              wrap(
                () =>
                  ssPartyAPI
                    .assignPartyRole(partyId, {
                      role_code: roleCode,
                    })
                    .then(() => setRoleCode("")),
                "Role assigned"
              )
            }
          >
            {subSaving ? "…" : "Assign"}
          </Button>
        </div>
      </Section>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Relationships Tab — view existing + create new relationship
// ─────────────────────────────────────────────────────────────────────────────
function RelationshipsTab({
  partyId,
  out,
  inn,
  relTypes,
  wrap,
  subSaving,
  onRefresh,
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    to_party_id: "",
    relationship_type_id: "",
    from_date: "",
    to_date: "",
  });

  const [searchQ, setSearchQ] = useState("");
  const [searchRes, setSearchRes] = useState([]);
  const [searching, setSearching] = useState(false);

  // Party search for the "to party" picker
  const doSearch = async (q) => {
    setSearchQ(q);

    if (!q || q.length < 2) {
      setSearchRes([]);
      return;
    }

    setSearching(true);
    try {
      const r = await ssPartyAPI.list({ q, size: 10 });
      setSearchRes(Array.isArray(r?.data) ? r.data : []);
    } catch {
      setSearchRes([]);
    } finally {
      setSearching(false);
    }
  };

  const save = () => {
    if (!form.to_party_id || !form.relationship_type_id) return;

    wrap(
      () =>
        ssRelAPI
          .create({
            from_party_id: partyId,
            to_party_id: parseInt(form.to_party_id),
            party_relationship_type_id: parseInt(
              form.relationship_type_id
            ),
            from_date: form.from_date || undefined,
            to_date: form.to_date || undefined,
          })
          .then(() => {
            setForm({
              to_party_id: "",
              relationship_type_id: "",
              from_date: "",
              to_date: "",
            });
            setSearchQ("");
            setSearchRes([]);
            setShowAdd(false);
            onRefresh?.();
          }),
      "Relationship created"
    );
  };

  const REL_COLS = [
    {
      key: "to_party_id",
      label: "Party",
      render: (v, r) => (
        <span style={{ fontWeight: 600 }}>
          {r.to_party_name || `#${v}`}
        </span>
      ),
    },
    {
      key: "type_code",
      label: "Type",
      render: (v) => <Badge color="purple">{v || "—"}</Badge>,
    },
    {
      key: "from_date",
      label: "From",
      render: (v) => (v ? v.split("T")[0] : "—"),
    },
    {
      key: "to_date",
      label: "To",
      render: (v) =>
        v ? (
          v.split("T")[0]
        ) : (
          <span style={{ color: COLOR.grayLight }}>Open</span>
        ),
    },
  ];

  const IN_COLS = [
    {
      key: "from_party_id",
      label: "Party",
      render: (v, r) => (
        <span style={{ fontWeight: 600 }}>
          {r.from_party_name || `#${v}`}
        </span>
      ),
    },
    {
      key: "type_code",
      label: "Type",
      render: (v) => <Badge color="blue">{v || "—"}</Badge>,
    },
    {
      key: "from_date",
      label: "From",
      render: (v) => (v ? v.split("T")[0] : "—"),
    },
    {
      key: "to_date",
      label: "To",
      render: (v) =>
        v ? (
          v.split("T")[0]
        ) : (
          <span style={{ color: COLOR.grayLight }}>Open</span>
        ),
    },
  ];

  // Subtab toggle: List ↔ Graph (audit High-6)
  const [view, setView] = useState('list');

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', gap: 4 }}>
          <Button
            size="sm"
            variant={view === 'list' ? 'primary' : 'ghost'}
            onClick={() => setView('list')}
          >📋 List</Button>
          <Button
            size="sm"
            variant={view === 'graph' ? 'primary' : 'ghost'}
            onClick={() => setView('graph')}
          >🕸 Graph</Button>
        </div>
        <Button size="sm" onClick={() => setShowAdd((s) => !s)}>
          {showAdd ? "Cancel" : "+ Add Relationship"}
        </Button>
      </div>

      {showAdd && (
        <Card style={{ marginBottom: 16, padding: 14 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              marginBottom: 10,
              color: COLOR.text,
            }}
          >
            New Relationship
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <Field label="Relationship Type">
              <Select
                value={form.relationship_type_id}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    relationship_type_id: e.target.value,
                  }))
                }
              >
                <option value="">— select type —</option>
                {relTypes.map((t) => (
                  <option
                    key={t.party_relationship_type_id || t.id}
                    value={
                      t.party_relationship_type_id || t.id
                    }
                  >
                    {t.party_relationship_type_name || t.code}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Search Party (to)">
              <div style={{ position: "relative" }}>
                <Input
                  value={searchQ}
                  onChange={(e) => doSearch(e.target.value)}
                  placeholder="Type name to search…"
                />

                {searchRes.length > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      zIndex: 100,
                      background: "#fff",
                      border: `1px solid ${COLOR.grayBorder}`,
                      borderRadius: RADIUS.md,
                      boxShadow:
                        "0 4px 12px rgba(0,0,0,.1)",
                      maxHeight: 180,
                      overflowY: "auto",
                    }}
                  >
                    {searchRes.map((p) => (
                      <div
                        key={p.partyid}
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          fontSize: 13,
                        }}
                        onClick={() => {
                          setForm((f) => ({
                            ...f,
                            to_party_id: p.partyid,
                          }));
                          setSearchQ(
                            p.display_name ||
                              p.name ||
                              `#${p.partyid}`
                          );
                          setSearchRes([]);
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>
                          {p.display_name || p.name}
                        </span>
                        <span
                          style={{
                            color: COLOR.textMuted,
                            marginLeft: 6,
                          }}
                        >
                          #{p.partyid}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {searching && (
                  <div
                    style={{
                      position: "absolute",
                      right: 8,
                      top: 8,
                      fontSize: 12,
                      color: COLOR.textMuted,
                    }}
                  >
                    …
                  </div>
                )}
              </div>
            </Field>

            <Field label="From Date">
              <Input
                type="date"
                value={form.from_date}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    from_date: e.target.value,
                  }))
                }
              />
            </Field>

            <Field label="To Date (leave blank = open)">
              <Input
                type="date"
                value={form.to_date}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    to_date: e.target.value,
                  }))
                }
              />
            </Field>
          </div>

          <Button
            size="sm"
            disabled={
              subSaving ||
              !form.to_party_id ||
              !form.relationship_type_id
            }
            onClick={save}
          >
            {subSaving ? "Saving…" : "Create Relationship"}
          </Button>
        </Card>
      )}

      {view === 'list' && (
        <>
          <Section
            title={`Outbound — This party → (${out.length})`}
          >
            <DataTable
              columns={REL_COLS}
              rows={out}
              rowKey={(_, i) => `out-${i}`}
              emptyIcon="🔗"
              emptyTitle="No outbound relationships"
            />
          </Section>

          <Section
            title={`Inbound — → This party (${inn.length})`}
            style={{ marginTop: 20 }}
          >
            <DataTable
              columns={IN_COLS}
              rows={inn}
              rowKey={(_, i) => `in-${i}`}
              emptyIcon="🔗"
              emptyTitle="No inbound relationships"
            />
          </Section>
        </>
      )}

      {view === 'graph' && (
        <SSRelationshipGraph
          centerId={partyId}
          onPick={(nextId) => {
            // Recenter the graph on the clicked node by dispatching the same
            // event SSGlobalSearch uses; the QuickActionsPanel listens for it.
            try {
              window.dispatchEvent(new CustomEvent('ss:party:open', {
                detail: { partyId: nextId },
              }));
            } catch {/* noop */}
          }}
        />
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Image Tab — upload + preview
// ─────────────────────────────────────────────────────────────────────────────
function ImageTab({ partyId, imageRef, wrap, subSaving }) {
  const [preview, setPreview] = useState(null);
  const [file, setFile]       = useState(null);

  const onFile = e => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = ev => setPreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  const upload = () => {
    if (!file) return;
    wrap(() => ssPartyAPI.uploadImage(partyId, file).then(() => { setFile(null); }), 'Image uploaded');
  };

  const imgUrl = ssPartyAPI.getImageUrl(partyId);

  return (
    <div>
      <Section title="Current Image">
        <img
          src={imgUrl}
          alt="Party"
          style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: RADIUS.lg, border: `1px solid ${COLOR.grayBorder}`, display: 'block' }}
          onError={e => { e.target.style.display = 'none'; }}
        />
      </Section>
      <Section title="Upload New Image" style={{ marginTop: 20 }}>
        <input ref={imageRef} type="file" accept="image/*" onChange={onFile} style={{ marginBottom: 10 }} />
        {preview && (
          <img src={preview} alt="Preview" style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: RADIUS.md, display: 'block', marginBottom: 10 }} />
        )}
        <Button size="sm" disabled={subSaving || !file} onClick={upload}>{subSaving ? 'Uploading…' : 'Upload'}</Button>
      </Section>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Custom Data Tab — JSON merge patch
// ─────────────────────────────────────────────────────────────────────────────
function CustomDataTab({ partyId, rawJson, wrap, subSaving }) {
  const [json, setJson]   = useState('');
  const [patch, setPatch] = useState('{}');
  const [jsonErr, setJsonErr] = useState('');

  useEffect(() => {
    try { setJson(JSON.stringify(JSON.parse(rawJson || '{}'), null, 2)); }
    catch { setJson(rawJson || ''); }
  }, [rawJson]);

  const applyPatch = () => {
    setJsonErr('');
    let parsed;
    try { parsed = JSON.parse(patch); } catch { setJsonErr('Invalid JSON'); return; }
    wrap(() => ssPartyAPI.patchCustomdata(partyId, parsed), 'Custom data updated');
  };

  return (
    <div>
      <Section title="Current Custom Data">
        <pre style={{ background: COLOR.grayBg, border: `1px solid ${COLOR.grayBorder}`, borderRadius: RADIUS.md, padding: 14, fontSize: 12, overflow: 'auto', maxHeight: 200 }}>{json || '{}'}</pre>
      </Section>
      <Section title="JSON Merge Patch" style={{ marginTop: 16 }}>
        <p style={{ fontSize: 13, color: COLOR.textMuted, margin: '0 0 8px' }}>Enter a JSON object to deep-merge into the existing custom data.</p>
        {jsonErr && <ErrorBanner>{jsonErr}</ErrorBanner>}
        <Textarea rows={6} value={patch} onChange={e => setPatch(e.target.value)} style={{ fontFamily: 'monospace', fontSize: 12 }} />
        <Button size="sm" style={{ marginTop: 10 }} disabled={subSaving} onClick={applyPatch}>{subSaving ? 'Applying…' : 'Apply Patch'}</Button>
      </Section>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
const PARTY_COLS = [
  { key: 'partyid', label: 'ID', render: v => <span style={{ color: COLOR.grayLight, fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>#{v}</span> },
  // Weight 600, not 700 — every row bold reads as a wall of black and
  // competes with the page title (Polar type scale: row emphasis = semibold).
  { key: 'display_name', label: 'Name', render: (v, r) => <span style={{ fontWeight: 600, fontSize: 13, color: COLOR.text }}>{v || r.name || '—'}</span> },
  { key: 'type', label: 'Type', render: v => <PartyTypePill type={v} /> },
  { key: 'business_roles', label: 'Roles', render: v => <PartyRoleChips roles={v || []} /> },
  { key: 'is_active', label: 'Status', render: v => <ActiveBadge active={v} /> },
];

export default function SSParties() {
  const [rows, setRows]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [loadErr, setLoadErr]   = useState('');   // list-load error banner
  const [page, setPage]         = useState(1);
  const PER = 50;

  // URL-driven filter seed — `/ss/parties?role_code=PATIENT` opens on a filtered view.
  const [searchParams] = useSearchParams();
  const [filters, setFilters]   = useState({
    q:              searchParams.get('q')              || '',
    type:           searchParams.get('type')           || '',
    role_code:      searchParams.get('role_code')      || '',
    include_closed: searchParams.get('include_closed') === 'true',
  });
  // Re-sync when user clicks a different sidebar preset (URL changes without remount)
  useEffect(() => {
    setFilters(f => ({
      ...f,
      q:              searchParams.get('q')              ?? f.q,
      type:           searchParams.get('type')           ?? f.type,
      role_code:      searchParams.get('role_code')      ?? f.role_code,
    }));
    setPage(1);
  }, [searchParams]);

  const [selected, setSelected]     = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createHint, setCreateHint] = useState(''); // post-create follow-up hint (e.g. nurse/doctor profile)

  // `/ss/parties?create=1` (sidebar shortcut) auto-opens the create modal
  useEffect(() => {
    if (searchParams.get('create') === '1') setShowCreate(true);
  }, [searchParams]);

  // Lookups — shared with detail drawer and create modal
  const [lookups, setLookups] = useState({ partyRoles: [], identTypes: [], contactMedia: [], contactMediaTypes: [], addrTypes: [], relTypes: [] });

  useEffect(() => {
    Promise.all([
      ssLookupAPI.partyRoles().catch(()            => ({ data: [] })),
      ssLookupAPI.identifierTypes().catch(()       => ({ data: [] })),
      ssLookupAPI.contactMedia().catch(()          => ({ data: [] })),
      ssLookupAPI.listContactMediaTypes().catch(() => ({ data: [] })),
      ssLookupAPI.addressTypes().catch(()          => ({ data: [] })),
      ssLookupAPI.listRelTypes().catch(()          => ({ data: [] })),
    ]).then(([pr, it, cm, cmt, at, rt]) => setLookups({
      partyRoles:        pr.data  || [],
      identTypes:        it.data  || [],
      contactMedia:      cm.data  || [],
      contactMediaTypes: cmt.data || [],
      addrTypes:         at.data  || [],
      relTypes:          rt.data  || [],
    }));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, size: PER };
    if (filters.q)              params.q              = filters.q;
    if (filters.type)           params.type           = filters.type;
    if (filters.role_code)      params.role_code      = filters.role_code;
    if (filters.include_closed) params.include_closed = true;

    ssPartyAPI.list(params)
      .then(r => {
        // Backend envelope: { success, data: [...parties], meta: { total, page, size } }
        // unwrap() in ssApi.js returns the HTTP body, so r.data is the parties array
        // and r.meta holds pagination counters.
        setLoadErr('');
        setRows(Array.isArray(r?.data) ? r.data : []);
        setTotal(r?.meta?.total ?? r?.data?.length ?? 0);
      })
      .catch(err => {
        const msg = err?.response?.data?.detail || err?.message || 'Failed to load parties';
        console.error('[SSParties] list failed:', err);
        setLoadErr(msg);
        setRows([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  // ── Optimistic-concurrency conflict handler ────────────────────────────────
  // ssApi.js fires `ssApiOnConflict` when a PUT/PATCH/DELETE returns 409/412/428.
  // Show a friendly modal asking the user to reload — preserves any in-flight
  // edits in component state so the user can copy them before the refresh.
  const [conflictInfo, setConflictInfo] = useState(null);
  useEffect(() => {
    const onConflict = (e) => setConflictInfo(e.detail);
    window.addEventListener('ssApiOnConflict', onConflict);
    return () => window.removeEventListener('ssApiOnConflict', onConflict);
  }, []);

  const hasFilters = filters.q || filters.type || filters.role_code;

  return (
    <div style={{ display: 'flex', gap: 20, height: '100%', fontFamily: FONT.family }}>

      {/* ── Left: list ── */}
      <div style={{ flex: selected ? '0 0 42%' : '1 1 100%', minWidth: 0, transition: 'flex .2s', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <PageHeader
          title="Party Explorer"
          subtitle={`${total.toLocaleString()} parties · ss* identity`}
          action={<Button onClick={() => setShowCreate(true)}>+ New Party</Button>}
        />

        {/* ── Global search (⌘K typeahead, grouped by entity kind) ── */}
        <SSGlobalSearch
          onPick={(partyId) => setSelected(partyId)}
          placeholder="Search parties, IDs, contacts… (⌘K)"
        />

        {/* Type tab strip — All | Persons | Orgs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: `2px solid ${COLOR.grayBorder}`, marginBottom: 2 }}>
          {[
            { value: '',    label: 'All' },
            { value: 'PER', label: '👤 Persons' },
            { value: 'ORG', label: '🏢 Orgs' },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setFilters(f => ({ ...f, type: opt.value }))}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '7px 16px', fontSize: 13, fontWeight: filters.type === opt.value ? 700 : 400,
                color: filters.type === opt.value ? COLOR.blue : COLOR.textMuted,
                borderBottom: filters.type === opt.value ? `2px solid ${COLOR.blue}` : '2px solid transparent',
                marginBottom: -2, transition: 'all .15s',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input style={{ maxWidth: 200 }} placeholder="Search name…" value={filters.q}
            onChange={e => setFilters(f => ({ ...f, q: e.target.value }))} />
          <Select style={{ maxWidth: 180 }} value={filters.role_code}
            onChange={e => setFilters(f => ({ ...f, role_code: e.target.value }))}>
            <option value="">All roles</option>
            {lookups.partyRoles.map(r => <option key={r.code} value={r.code}>{r.name}</option>)}
          </Select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: COLOR.textMuted }}>
            <input type="checkbox" checked={filters.include_closed}
              onChange={e => setFilters(f => ({ ...f, include_closed: e.target.checked }))} />
            Include closed
          </label>
          {hasFilters && <Button variant="ghost" size="sm" onClick={() => setFilters({ q: '', type: '', role_code: '', include_closed: false })}>Clear</Button>}
        </div>

        {loadErr && <ErrorBanner>{loadErr}</ErrorBanner>}
        {createHint && <SuccessBanner onClose={() => setCreateHint('')}>{createHint}</SuccessBanner>}

        <Card style={{ padding: 0, flex: 1 }}>
          <DataTable
            columns={[
              ...PARTY_COLS,
              { key: '_view', label: '', render: (_, r) => (
                <Button variant="ghostBlue" size="sm" onClick={e => { e.stopPropagation(); setSelected(r.partyid); }}>View →</Button>
              )},
            ]}
            rows={rows}
            rowKey="partyid"
            loading={loading}
            onRowClick={r => setSelected(r.partyid)}
            selectedKey={selected}
            emptyIcon={filters.type === 'ORG' ? '🏢' : '👥'}
            emptyTitle={filters.type === 'ORG' ? 'No organisation parties yet' : 'No parties found'}
            emptyMsg={
              filters.type === 'ORG'
                ? 'Organisation parties (hospitals, insurers, partner orgs) are created here via "+ New Party → Organisation". Note: tenant companies registered under Settings → Organizations live in a separate registry and do not appear in the Party Explorer.'
                : (hasFilters ? 'Try adjusting your filters.' : 'Create your first party above.')
            }
          />
        </Card>
        <Pagination page={page} total={total} per={PER} onPage={setPage} />
      </div>

      {/* ── Centre: detail drawer ── */}
      {selected && (
        <div style={{ flex: '1 1 38%', minWidth: 0, background: '#fff', border: `1px solid ${COLOR.grayBorder}`, borderRadius: RADIUS.lg, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <DetailDrawer
            partyId={selected}
            onClose={() => setSelected(null)}
            onRefresh={load}
            lookups={lookups}
          />
        </div>
      )}

      {/* ── Right: quick-actions panel ── */}
      {selected && (
        <QuickActionsPanel
          partyId={selected}
          onClose={() => setSelected(null)}
          onRefresh={load}
        />
      )}

      {/* ── Create modal ── */}
      <PartyFormModal
        open={showCreate}
        initial={null}
        lookups={lookups}
        defaultRoleCode={filters.role_code || ''}
        onClose={() => setShowCreate(false)}
        onDone={(_partyId, hint) => { setShowCreate(false); setCreateHint(typeof hint === 'string' ? hint : ''); load(); }}
      />

      {/* ── Optimistic-concurrency conflict modal ── */}
      {conflictInfo && (
        <Modal
          open
          onClose={() => setConflictInfo(null)}
          title="Someone else changed this record"
          width={520}
        >
          <div style={{ fontSize: 14, color: COLOR.text, lineHeight: 1.5 }}>
            <p>
              The record you tried to {conflictInfo.method?.toUpperCase() || 'update'} has been
              modified since you opened it (HTTP {conflictInfo.status}). Your changes were
              <strong> not saved</strong>.
            </p>
            <p style={{ color: COLOR.textMuted, fontSize: 13 }}>
              Reload to pull the latest version, then re-apply your edit. If you have unsaved
              text, copy it elsewhere before reloading.
            </p>
          </div>
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button variant="ghost" size="sm" onClick={() => setConflictInfo(null)}>
              Dismiss
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setConflictInfo(null);
                load();
                if (selected) setSelected(selected); // forces drawer reload
              }}
            >
              Reload now
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// QuickActionsPanel — right-side context panel (spec §4.3)
// Quick-action buttons + last-10 activity mini-timeline. Collapsible.
// ─────────────────────────────────────────────────────────────────────────────
function QuickActionsPanel({ partyId, onClose, onRefresh }) {
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(true);
  const [linkOrgOpen, setLinkOrgOpen] = useState(false);

  useEffect(() => {
    if (!partyId) return;
    setLoading(true);
    ssPartyAPI.auditHistory(partyId, { limit: 10 })
      .then(r => setEvents((r?.data || []).slice(0, 10)))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [partyId]);

  const QUICK_ACTIONS = [
    { icon: '📍', label: '+ Address',      tab: 'addresses' },
    { icon: '📞', label: '+ Contact',      tab: 'contacts' },
    { icon: '🪪', label: '+ Identifier',   tab: 'identifiers' },
    { icon: '🏷️', label: '+ Role',         tab: 'roles' },
    { icon: '🔗', label: '+ Relationship', tab: 'rels' },
    { icon: '📎', label: '+ Document',     tab: 'documents' },
  ];

  const ACTION_COLORS = {
    INSERT: '#16a34a', UPDATE: '#2563eb', DELETE: '#dc2626',
    MERGE: '#7c3aed', CLOSE: '#d97706', OPEN: '#16a34a',
  };

  const fmtRel = (iso) => {
    if (!iso) return '—';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60)  return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)   return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  if (!visible) {
    return (
      <div style={{ width: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 12 }}>
        <button onClick={() => setVisible(true)} title="Expand panel"
          style={{ background: 'none', border: `1px solid ${COLOR.grayBorder}`, borderRadius: RADIUS.md,
                   padding: '6px 4px', cursor: 'pointer', fontSize: 16, color: COLOR.textMuted }}>
          ‹
        </button>
      </div>
    );
  }

  return (
    <div style={{
      flex: '0 0 210px', minWidth: 0, display: 'flex', flexDirection: 'column',
      background: '#f8fafc', border: `1px solid ${COLOR.grayBorder}`,
      borderRadius: RADIUS.lg, overflow: 'hidden',
    }}>
      {/* Panel header */}
      <div style={{ padding: '12px 14px', borderBottom: `1px solid ${COLOR.grayBorder}`,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: COLOR.textMuted,
                       letterSpacing: '.05em', textTransform: 'uppercase' }}>
          Quick Actions
        </span>
        <button onClick={() => setVisible(false)} title="Collapse"
          style={{ background: 'none', border: 'none', cursor: 'pointer',
                   fontSize: 14, color: COLOR.textMuted }}>
          ›
        </button>
      </div>

      {/* Action buttons */}
      <div style={{ padding: '10px 10px 4px' }}>
        {QUICK_ACTIONS.map(a => (
          <button key={a.tab}
            onClick={() => window.dispatchEvent(
              new CustomEvent('ss:party:tab', { detail: { partyId, tab: a.tab } })
            )}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%',
              background: 'none', border: `1px solid ${COLOR.grayBorder}`,
              borderRadius: RADIUS.md, padding: '7px 10px', marginBottom: 5,
              cursor: 'pointer', fontSize: 13, color: COLOR.text, textAlign: 'left',
              transition: 'background .1s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = COLOR.blueBg}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >
            <span>{a.icon}</span><span>{a.label}</span>
          </button>
        ))}
        <button
          onClick={() => setLinkOrgOpen(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, width: '100%',
            background: 'none', border: `1px solid ${COLOR.grayBorder}`,
            borderRadius: RADIUS.md, padding: '7px 10px', marginBottom: 5,
            cursor: 'pointer', fontSize: 13, color: COLOR.text, textAlign: 'left',
            transition: 'background .1s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = COLOR.blueBg}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
        >
          <span>🏢</span><span>Link to Org</span>
        </button>
      </div>

      {/* Activity mini-timeline */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 10px 10px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: COLOR.textMuted,
                      letterSpacing: '.05em', textTransform: 'uppercase', padding: '8px 0 6px' }}>
          Recent Activity
        </div>
        {loading && <div style={{ fontSize: 12, color: COLOR.textMuted }}>Loading…</div>}
        {!loading && events.length === 0 && (
          <div style={{ fontSize: 12, color: COLOR.textMuted }}>No recent activity</div>
        )}
        {events.map((ev, i) => {
          const action = ev.eventtype || ev.action || 'UPDATE';
          const color  = ACTION_COLORS[action] || COLOR.textMuted;
          return (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'flex-start' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: color,
                            marginTop: 4, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLOR.text }}>
                  {action}
                  {ev.tablename && (
                    <span style={{ fontWeight: 400, color: COLOR.textMuted }}> · {ev.tablename}</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: COLOR.textMuted }}>
                  {fmtRel(ev.occurredat || ev.sys_entry_date)}
                  {ev.actoruserid ? ` · ${ev.actoruserid}` : ''}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {linkOrgOpen && (
        <LinkToOrgModal
          partyId={partyId}
          onClose={() => setLinkOrgOpen(false)}
          onDone={() => { setLinkOrgOpen(false); onRefresh?.(); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LinkToOrgModal — quick relationship shortcut to link a party to an org
// ─────────────────────────────────────────────────────────────────────────────
function LinkToOrgModal({ partyId, onClose, onDone }) {
  const [orgs, setOrgs]       = useState([]);
  const [orgId, setOrgId]     = useState('');
  const [relType, setRelType] = useState('EMPLOYED_BY');
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState('');

  useEffect(() => {
    ssPartyAPI.list({ type: 'ORG', per_page: 200 })
      .then(r => setOrgs(r?.data?.items || r?.data || []))
      .catch(() => {});
  }, []);

  const save = async () => {
    if (!orgId) { 
      setErr('Select an organisation'); 
      return; 
    }

    setSaving(true); 
    setErr('');

    try {
      await ssRelAPI.create({
        from_party_id:     partyId,
        to_party_id:       parseInt(orgId),
        relationship_type: relType,
      });

      onDone();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Failed to link');
    } finally { 
      setSaving(false); 
    }
  };

  return (
    <Modal open onClose={onClose} title="Link to Organisation">
      {err && <ErrorBanner>{err}</ErrorBanner>}

      <Field label="Organisation">
        <Select value={orgId} onChange={e => setOrgId(e.target.value)}>
          <option value="">— select —</option>
          {orgs.map(o => (
            <option key={o.partyid} value={o.partyid}>
              {o.name || o.display_name}
            </option>
          ))}
        </Select>
      </Field>

      <Field label="Relationship type">
        <Select value={relType} onChange={e => setRelType(e.target.value)}>
          <option value="EMPLOYED_BY">Employed By</option>
          <option value="MEMBER_OF">Member Of</option>
          <option value="AFFILIATED_WITH">Affiliated With</option>
          <option value="CONTRACTED_BY">Contracted By</option>
        </Select>
      </Field>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Link'}
        </Button>
      </div>
    </Modal>
  );
}
